import type {
  Asset,
  Cue,
  CueEffect,
  Hotspot,
  NormRect,
  PhoneFrame,
  PhoneTrack,
  ScrollSpec,
  Shot,
  ShotReason,
  Step,
  Theme,
} from './demo';
import {
  computeLayout,
  DEFAULT_MEDIAN_TEXT_H,
  ESCALATE_MARGIN,
  MAX_FOCUS_ZOOM,
  MIN_FOCUS_ZOOM,
  MIN_TEXT_PX,
  resolveFocus,
  unionRects,
  type Layout,
  type LayoutInput,
  type Viewport,
} from './geometry';

/**
 * The cinematic camera — how a step is framed when `theme.motion` is
 * `'cinematic'`, and how the framing moves between steps.
 *
 * `computeLayout` (geometry.ts) answers one question: is the text readable,
 * and if not, how far must we zoom to make it so. That is a floor, and it is
 * what a desktop capture on a phone needs. It says nothing about *showing* a
 * step: on a desktop-sized embed a 1440px screen fits and stays fitted, so a
 * demo reads as a slideshow of whole screens with a pulsing dot somewhere on
 * each. The camera eases into the region the step is about — the resolved
 * focus — at a zoom that keeps context, and moves on from there.
 *
 * Shared by player, editor preview (which mounts the real player) and the MP4
 * export, which composes frames with sharp: the framing must be identical in
 * all three or the preview and the video lie about the embed. Pure functions,
 * no zod, so the player can import it from `@mado/schema/camera` inside its
 * 60 KB budget.
 *
 * Everything here is a `Layout` — one `transform` (translate + scale) on the
 * stage, as before. The camera adds no property that animates; it changes the
 * values the existing transform tweens between (DESIGN-SYSTEM §5 rule 1).
 */

export type Motion = NonNullable<Theme['motion']>;

/**
 * The subject fills this much of the viewport on its tighter axis. The
 * subject is the step's own targets (a button, a field), not the derived
 * focus rect: that carries `FOCUS_PAD` for phone centring, and with it a wide
 * input field "fills" the frame at 1x and the camera never moves, while a
 * corner button overshoots. An authored focus rect is the subject as drawn.
 */
export const CAMERA_FILL = 0.5;
/**
 * The camera never zooms past this multiple of fit-scale on its own account.
 * Judged in a browser against the harness: at 1.8x a corner button on a
 * desktop screen took the camera past the sidebar and the page title — the
 * screen stopped being recognisably itself. At 1.5x two thirds of the width
 * is still on show and the target is unmistakably the subject. Legibility
 * (`computeLayout`, up to `MAX_FOCUS_ZOOM`) may still zoom further: that is a
 * floor the camera sits on, not a choice.
 */
export const CAMERA_MAX_ZOOM = 1.5;
/**
 * A zoom below this is not a camera move, it is a crop: the edges go and
 * nothing gets bigger to the eye. Below it, the step is shown whole.
 */
export const CAMERA_MIN_ZOOM = 1.12;
/** A focus this close to the whole frame means "show the whole screen". */
export const FULL_FRAME_EPS = 0.02;
/**
 * Room kept before the leading edge of a subject that is framed from its
 * start rather than its centre (a text field wider than the viewport), so
 * the field's own border is on screen and the first character typed does
 * not sit on the edge of the glass.
 */
export const LEAD_PAD_PX = 16;

/** How long the camera takes to settle on a new screen's target. */
export const CAMERA_SETTLE_MS = 640;
/** A pan within one screen: proportional to distance, within these bounds. */
export const CAMERA_PAN_MIN_MS = 420;
export const CAMERA_PAN_MAX_MS = 760;
export const CAMERA_PAN_MS_PER_PX = 0.9;
/** The spotlight fades in over this, once the camera has settled. */
export const SPOTLIGHT_MS = 360;
/** The spotlight's clear window, beyond the target, in CSS px. */
export const SPOTLIGHT_PAD_PX = 26;
/** Soft edge of the spotlight, in CSS px — wide, so it reads as light, not a hole. */
export const SPOTLIGHT_FEATHER_PX = 84;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** The full-frame rect — what "show the whole screen" is stored as. */
export const FULL_FRAME: NormRect = { x: 0, y: 0, w: 1, h: 1 };

/** True when a focus rect covers (nearly) the whole asset: the author asked for no zoom. */
export function isFullFrame(r: NormRect | null | undefined): boolean {
  if (!r) return false;
  return r.w >= 1 - FULL_FRAME_EPS && r.h >= 1 - FULL_FRAME_EPS;
}

export interface CameraInput extends LayoutInput {
  /** `theme.motion`. Absent (older documents) is `'static'`: exactly `computeLayout`. */
  motion?: Motion | undefined;
  /**
   * What the camera frames — see `cameraSubject`. Absent, the focus rect
   * stands in (an authored focus is its own subject).
   */
  subject?: NormRect | null | undefined;
  /**
   * Frame the subject from its leading edge when it is wider than the
   * viewport, instead of its centre. Set for a step that types into its
   * target: a text field is read, and filled, from its start, and centred
   * on a phone the first characters typed land off the left edge of the
   * glass. A subject that fits is centred as usual.
   */
  alignStart?: boolean | undefined;
}

/**
 * What a step's camera frames: an authored focus rect as drawn ("zoom
 * here"), else the union of the step's targets. Null when there is nothing —
 * the camera then shows the whole screen.
 */
export function cameraSubject(step: Step): NormRect | null {
  if (step.focus) return step.focus;
  return spotlightRect(step);
}

/**
 * The framing a step rests at.
 *
 * `static` (and every document without `theme.motion`) is `computeLayout`,
 * byte for byte — a published demo's framing does not change because the
 * player learned a new trick. `cinematic` starts from that layout as a floor
 * and zooms into the focus rect until it fills `CAMERA_FILL` of the viewport,
 * capped at `CAMERA_MAX_ZOOM`, never revealing empty space beside the asset.
 */
export function cameraLayout(input: CameraInput): Layout {
  const base = computeLayout(input);
  if (input.motion !== 'cinematic') return base;
  const { viewport: vp, asset, focus } = input;
  const subject = input.subject === undefined ? focus : input.subject;
  if (!focus || !subject || isFullFrame(focus) || input.allowFocus === false) return base;

  const containScale = base.containScale;
  // Zoom so the subject's tighter axis fills CAMERA_FILL of the viewport.
  const byW = (CAMERA_FILL * vp.w) / (subject.w * asset.w);
  const byH = (CAMERA_FILL * vp.h) / (subject.h * asset.h);
  let scale = Math.min(byW, byH, containScale * CAMERA_MAX_ZOOM);
  // Never less than legibility needs; never a zoom too small to be a zoom.
  if (scale < base.scale) scale = base.scale;
  if (scale < containScale * CAMERA_MIN_ZOOM) return base;

  // Centre on the subject; the focus rect (padded, floored) is what the
  // legibility layout centres on, and here it would pull a corner target
  // towards the middle of the screen instead of framing it.
  return frame(
    vp,
    asset,
    subject,
    scale,
    containScale,
    input.asset.medianTextH,
    input.alignStart === true,
  );
}

/**
 * `cameraLayout` for a step: focus and subject resolved from it. A cinematic zoom then has its
 * edges cleaned (`cleanFrame`, the step's `stepLines`), so no side of the window runs through a
 * word; `clean: false` skips that (a phone story's picture, which frames its own way).
 */
export function stepCamera(
  step: Step,
  input: Omit<CameraInput, 'focus' | 'subject' | 'alignStart'> & { clean?: boolean },
): Layout {
  const { clean, ...rest } = input;
  const cam: CameraInput = {
    ...rest,
    focus: resolveFocus(step),
    subject: cameraSubject(step),
    lookAt: spotlightRect(step),
    alignStart: !!step.input,
  };
  const l = cameraLayout(cam);
  if (clean === false || cam.motion !== 'cinematic' || l.mode !== 'focus') return l;
  const base = computeLayout(cam);
  if (l.scale <= base.scale + 1e-9) return l;
  return cleanFrame(l, cam.viewport, cam.asset, stepLines(step), {
    floor: base,
    keep: cam.subject ?? null,
    padPx: LEAD_PAD_PX,
  });
}

/** `restLayout` for a step: its focus resolved, and its targets to look at
 *  when the whole screen has to be zoomed for legibility (`LayoutInput.lookAt`). */
export function stepRest(
  step: Step,
  input: Omit<CameraInput, 'focus' | 'subject' | 'alignStart' | 'lookAt'>,
): Layout {
  return restLayout({ ...input, focus: resolveFocus(step), lookAt: spotlightRect(step) });
}

/**
 * Where a subject wider than the viewport sits horizontally: `alignStart`
 * puts its leading edge `LEAD_PAD_PX` in from the left; otherwise its centre
 * is the viewport's. Either way pulled back so no empty space shows beside
 * the asset. Shared by the step camera and the scroll layout.
 */
function alignX(vp: Viewport, focus: NormRect, sw: number, alignStart: boolean): number {
  if (sw <= vp.w) return (vp.w - sw) / 2;
  if (alignStart && focus.w * sw > vp.w - 2 * LEAD_PAD_PX) {
    return clamp(LEAD_PAD_PX - focus.x * sw, vp.w - sw, 0);
  }
  return clamp(vp.w / 2 - (focus.x + focus.w / 2) * sw, vp.w - sw, 0);
}

/** Centre `focus` at `scale`, pulled back so no empty space shows beside the asset. */
function frame(
  vp: { w: number; h: number },
  asset: { w: number; h: number },
  focus: NormRect,
  scale: number,
  containScale: number,
  medianTextH: number | undefined,
  alignStart = false,
): Layout {
  const sw = asset.w * scale;
  const sh = asset.h * scale;
  const cy = (focus.y + focus.h / 2) * sh;
  const tx = alignX(vp, focus, sw, alignStart);
  const ty = sh <= vp.h ? (vp.h - sh) / 2 : clamp(vp.h / 2 - cy, vp.h - sh, 0);
  return {
    mode: 'focus',
    scale,
    tx,
    ty,
    containScale,
    textPx: (medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h * scale,
  };
}

/**
 * The framing a camera move starts from or pulls back to: the step's static
 * layout. On a desktop-sized embed that is the whole screen; on a phone it is
 * the legibility zoom, which is as far out as a phone may go.
 */
export function restLayout(input: CameraInput): Layout {
  return computeLayout(input);
}

export interface ScrollLayoutInput {
  viewport: Viewport;
  asset: Pick<Asset, 'w' | 'h'> & { medianTextH?: number | undefined };
  /**
   * The step's authored focus, if any. A scroll step has no targets to
   * derive one from; this only says where to look *across* the screen when
   * the zoom is wider than the viewport (a terminal's left column, a list
   * that is not centred on the page). Its height is ignored: the scroll
   * decides the vertical position.
   */
  focus: NormRect | null;
  /** As `LayoutInput.allowFocus`: false for a native phone capture on a phone. */
  allowFocus?: boolean;
}

/**
 * How a scroll step's tall asset sits at `progress` (0..1 through the
 * step's own `from..to` range).
 *
 * The asset fills the viewport width — that is what "scrolling this screen"
 * means — and progress drives the vertical offset; it also always fills the
 * viewport height, so nothing shows below a short one. A wide capture (web,
 * desktop, terminal) fitted to a phone's width is a strip of unreadable
 * text with nothing to scroll, so below the legibility line the same rule
 * `computeLayout` applies to a still step applies here: zoom to where body
 * text reaches `MIN_TEXT_PX`, capped at `MAX_FOCUS_ZOOM`, with the same
 * hysteresis. The tall asset then also overflows sideways, and the viewer
 * looks at the authored focus (its start, when it is itself wider than the
 * viewport — a column of text is read from its left), or at the middle.
 *
 * Shared by the player (`stage.ts`) and the MP4 export (`frame.ts`
 * `composeScrollFrame`), so the video scrolls what the embed scrolls.
 */
export function scrollLayout(
  input: ScrollLayoutInput,
  scroll: Pick<ScrollSpec, 'from' | 'to'>,
  progress: number,
): Layout {
  const { viewport: vp, asset } = input;
  const fit = vp.w / asset.w;
  const medianTextH = asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H;
  let scale = fit;
  const allowFocus = input.allowFocus ?? true;
  if (allowFocus && medianTextH * asset.h * fit < MIN_TEXT_PX * ESCALATE_MARGIN) {
    const needed = clamp(MIN_TEXT_PX / (medianTextH * asset.h), fit, fit * MAX_FOCUS_ZOOM);
    if (needed >= fit * MIN_FOCUS_ZOOM) scale = needed;
  }
  // Never shorter than the viewport. A wide capture fitted to a portrait
  // phone's width can leave the tall asset shorter than the glass, and
  // top-aligned that is a band of black under the picture with nothing to
  // scroll. Cover the height instead: the sides crop (the focus, or the
  // middle, stays on screen), the sticky bands still sit on the picture at
  // the viewport's edges, and no background ever shows below it. A native
  // phone capture's stitched page is taller than its screen, so this never
  // crops one in practice.
  if (asset.h * scale < vp.h) scale = vp.h / asset.h;
  const sw = asset.w * scale;
  const sh = asset.h * scale;
  const focus = input.focus ?? FULL_FRAME;
  const tx = alignX(vp, focus, sw, focus.w * sw > vp.w && !isFullFrame(focus));
  const range = Math.max(0, sh - vp.h);
  const at = scroll.from + (scroll.to - scroll.from) * clamp(progress, 0, 1);
  return {
    mode: scale > fit ? 'focus' : 'contain',
    scale,
    tx,
    ty: -at * range,
    containScale: fit,
    textPx: medianTextH * asset.h * scale,
  };
}

/** Linear interpolation between two layouts — for renderers that draw frames themselves. */
export function lerpLayout(a: Layout, b: Layout, t: number): Layout {
  const k = clamp(t, 0, 1);
  return {
    mode: k < 1 ? a.mode : b.mode,
    scale: a.scale + (b.scale - a.scale) * k,
    tx: a.tx + (b.tx - a.tx) * k,
    ty: a.ty + (b.ty - a.ty) * k,
    containScale: b.containScale,
    textPx: a.textPx + (b.textPx - a.textPx) * k,
  };
}

/** Two layouts that put the same pixels in the same place. */
export function sameLayout(a: Layout, b: Layout, eps = 0.5): boolean {
  return (
    Math.abs(a.scale - b.scale) < 1e-4 && Math.abs(a.tx - b.tx) < eps && Math.abs(a.ty - b.ty) < eps
  );
}

/**
 * How long a pan between two framings of one screen takes: proportional to how
 * far the picture travels on screen, within bounds, so a nudge is quick and a
 * sweep across a wide screen is not a snap.
 */
export function panDuration(from: Layout, to: Layout, viewport: { w: number; h: number }): number {
  // Travel of the viewport centre, measured in the destination's pixels.
  const cx = viewport.w / 2;
  const cy = viewport.h / 2;
  const ax = (cx - from.tx) / from.scale;
  const ay = (cy - from.ty) / from.scale;
  const bx = (cx - to.tx) / to.scale;
  const by = (cy - to.ty) / to.scale;
  const travel = Math.hypot(ax - bx, ay - by) * to.scale;
  const zoom = Math.abs(Math.log(to.scale / from.scale)) * Math.min(viewport.w, viewport.h);
  return clamp(
    Math.round((travel + zoom) * CAMERA_PAN_MS_PER_PX),
    CAMERA_PAN_MIN_MS,
    CAMERA_PAN_MAX_MS,
  );
}

/**
 * What the spotlight leaves lit: the step's targets (hotspots, an anchored
 * tooltip), not its focus — the focus carries padding and floors that would
 * light up half the screen. Null when the step has nothing to point at.
 */
export function spotlightRect(step: Step): NormRect | null {
  if (step.scroll) return null;
  const rects: NormRect[] = step.hotspots.map((h) => h.geom);
  const a = step.annotation;
  if (a && typeof a.anchor !== 'string') rects.push(a.anchor);
  return unionRects(rects.filter((r) => !isDubiousTarget(r)));
}

/**
 * What a phone-story close-up (`Shot` reason `target`) frames when a step
 * has more than one hotspot (CLAUDE.md "several hotspots per step"): several
 * targets can sit far apart on the screen, and a close-up built from their
 * union — right for the camera and the spotlight, which light every target
 * at once — would zoom out to fit all of them and stop being a close-up. The
 * step's tooltip already singles one out when it names one; otherwise the
 * first hotspot drawn is "the" target. A step with at most one hotspot has
 * nothing to union in the first place, so this is `spotlightRect` unchanged
 * for the common case — used by `storyBeats` below and by
 * `@mado/autofocus/shots` `shotSubject` at draft time.
 */
export function storySubject(step: Step): NormRect | null {
  if (step.hotspots.length <= 1) return spotlightRect(step);
  const a = step.annotation;
  if (a && typeof a.anchor === 'string') {
    const named = step.hotspots.find((h) => h.id === a.anchor);
    if (named && !isDubiousTarget(named.geom)) return named.geom;
  }
  const first = step.hotspots.find((h) => !isDubiousTarget(h.geom));
  return first ? first.geom : spotlightRect(step);
}

/**
 * A target wider than this share of the screen is a row, a band or a whole
 * surface (a swipe area, a guessed click box across a table), not a control:
 * lighting it or zooming onto it frames nothing.
 */
export const DUBIOUS_TARGET_W = 0.6;
/**
 * Thinner than this share of the screen on either axis is a sliver — about
 * 9px of a 900px desktop capture, below any real control. Zoomed and lit it
 * reads as two orange lines on a dark screen.
 */
export const DUBIOUS_TARGET_MIN = 0.01;

/**
 * A target the camera and the spotlight should not trust: too wide to be one
 * control, or too thin to be one. It stays tappable (the overlay draws every
 * hotspot); it only gets no zoom and no light.
 */
export function isDubiousTarget(r: NormRect): boolean {
  return r.w > DUBIOUS_TARGET_W || r.w < DUBIOUS_TARGET_MIN || r.h < DUBIOUS_TARGET_MIN;
}

/**
 * The transition a step enters with when the document does not say: on a
 * cinematic demo a step reached by a click grows from that click; anything
 * else, and every static demo, fades.
 */
export function defaultTransition(
  motion: Motion | undefined,
  viaHotspot: boolean,
): 'reveal' | 'fade' {
  return motion === 'cinematic' && viaHotspot ? 'reveal' : 'fade';
}

/* ── phone story ────────────────────────────────────────────────────────────
 *
 * A landscape capture (a web app, a desktop program) on a portrait phone.
 * Fitted, it is a postage stamp; zoomed to legible text (`computeLayout`) it
 * is a 28%-wide keyhole, one per step, with a tooltip bubble over what it was
 * meant to explain. The story layout splits the screen instead: a picture
 * area above (or below) a caption band, the tooltip's text moved into the
 * band, and the picture walks through the step's `shots` — the whole screen
 * or what just changed, then a legible close-up of the control, where its
 * hotspot comes alive.
 *
 * Only a document whose steps carry `shots` tells a story, and only on a
 * portrait viewport showing a landscape source. Everything else — every
 * document published before shots existed, any desktop or tablet embed, any
 * phone capture — frames exactly as before. Pure functions, shared by the
 * player and the 9:16 social cut. */

/** A rect in viewport CSS px (in the MP4 export: output px). */
export interface PxRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A viewport this much taller than wide is portrait enough for a story. */
export const STORY_PORTRAIT = 1.15;
/** The caption band never gets shorter than this… */
export const CAPTION_MIN_PX = 84;
/** …or taller than this share of the viewport; the picture is the point. */
export const CAPTION_MAX_SHARE = 0.34;
/** The player's control bar at the bottom — the picture keeps clear of it. */
export const STORY_CHROME_PX = 56;
/**
 * A target whose centre sits above this share of the picture's height puts
 * the caption band on top: the caption stays next to what it explains, so
 * the eye does not cross the whole screen between reading and tapping.
 */
export const CAPTION_TOP_BELOW = 0.42;
/** How long the camera rests on a lead-in shot before it moves to the target. */
export const SHOT_HOLD_MS: Record<ShotReason, number> = {
  establish: 1300,
  result: 1100,
  target: 0,
};
/**
 * A close-up never enlarges body text past this: a shot drawn round a lone
 * icon would otherwise zoom 14px UI text to 60px and lose the screen.
 */
export const SHOT_MAX_TEXT_PX = 20;
/** A block wider than the picture is read from its left edge for this long before the camera pans to the target. */
export const READ_HOLD_MS = 1000;
/** Only when the pan to the target is at least this far (CSS px); a nudge is not worth a beat. */
export const READ_PAN_MIN_PX = 48;
/** How long the whole screen is held before the first zoom, and before the end card. */
export const OVERVIEW_HOLD_MS = 1800;
/** Air kept before the left edge of a close-up that is wider than the picture. */
export const SHOT_LEAD_PX = 12;
/** Generated close-ups aim a little above the legibility floor, for comfort. */
export const SHOT_TEXT_PX = 12.5;

/** A story only makes sense for a landscape source on a portrait viewport. */
export function isStoryViewport(
  viewport: Viewport,
  source: { captureW: number; captureH: number },
): boolean {
  return source.captureW > source.captureH && viewport.h >= viewport.w * STORY_PORTRAIT;
}

/** The document has something to tell a story with. */
export function hasShots(doc: { steps: Step[] }): boolean {
  return doc.steps.some((s) => !s.scroll && !!s.shots?.length);
}

/**
 * The document is told as a story on a portrait viewport: it has shots, or
 * it is a landscape web/desktop capture without any (published before shots
 * existed, or made "for desktop" and opened on a phone anyway). Without
 * shots each step walks `fallbackShots` instead — the whole screen, then its
 * target — never the old legibility zoom, which on a 2560px capture is a
 * slice with text cut off on both sides. A phone capture that happens to be
 * landscape (a game) keeps its own framing, as `wantsShots` decides at
 * publish. Whether a given viewport is portrait is `isStoryViewport`.
 */
export function storyDoc(doc: {
  steps: Step[];
  source: { kind?: string; captureW: number; captureH: number };
  assets?: Record<string, Pick<Asset, 'w' | 'h'>>;
}): boolean {
  if (hasShots(doc)) return true;
  const src = storySource(doc);
  return doc.source.kind !== 'mobile' && src.captureW > src.captureH;
}

/**
 * The capture's screen size, as the story reads it. `doc.source` is usually
 * that, but a screenshot set whose first image was a full-page (stitched,
 * scrolled) capture — the capture extension's "Full page", or a scroll as its
 * first step — got its `captureW`/`captureH` from that tall image
 * (`assembleImageSet` takes the first image's size). A web or desktop source
 * then reads as portrait, and the whole demo lost its phone story: the plain
 * full-viewport layout, a page's middle at cover zoom (often blank), and the
 * page's own bottom bar drawn under the player's controls. Published
 * documents are immutable, so this is read, never rewritten: a non-mobile
 * source that is a scroll step's own tall image takes the size of the first
 * ordinary screen instead, or — every step a scroll — the page's width over a
 * desktop window's height (16:10). Anything else is `doc.source` unchanged.
 */
export function storySource(doc: {
  steps: Step[];
  source: { kind?: string; captureW: number; captureH: number };
  assets?: Record<string, Pick<Asset, 'w' | 'h'>>;
}): { captureW: number; captureH: number } {
  const src = doc.source;
  if (src.kind === 'mobile' || src.captureW > src.captureH || !doc.assets) return src;
  const tall = doc.steps.some((s) => {
    if (!s.scroll) return false;
    const a = doc.assets![s.scroll.assetTall];
    return !!a && a.w === src.captureW && a.h === src.captureH;
  });
  if (!tall) return src;
  for (const s of doc.steps) {
    if (s.scroll) continue;
    const a = doc.assets[s.asset];
    if (a && a.w > 0 && a.h > 0) return { captureW: a.w, captureH: a.h };
  }
  return { captureW: src.captureW, captureH: Math.round(src.captureW / 1.6) };
}

/**
 * The shots a story step without `shots` walks, derived from the document
 * alone (no pixels at runtime): the whole screen, then — when the step has
 * something to point at (its target, else a real focus) — a legible
 * close-up of it. With nothing to point at, the whole screen is the step: a
 * complete picture under its caption, never a guessed crop.
 */
export function fallbackShots(
  step: Step,
  /** With both, the close-up is a window at `SHOT_TEXT_PX` round the subject
   *  (the zoom a generated shot uses), not the subject alone — a lone button
   *  would otherwise zoom to `SHOT_MAX_TEXT_PX` and lose its surroundings. */
  asset?: ShotAsset,
  picture?: PxRect,
): Shot[] {
  if (step.scroll) return [];
  const establish: Shot = { reason: 'establish', rect: FULL_FRAME };
  const focus = step.focus && !isFullFrame(step.focus) ? step.focus : null;
  const subject = storySubject(step) ?? focus;
  if (!subject) return [establish];
  let rect = subject;
  if (asset && picture) {
    const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
    const scale = Math.max(
      Math.min(picture.w / asset.w, picture.h / asset.h),
      SHOT_TEXT_PX / textH,
    );
    const w = Math.min(1, Math.max(subject.w, picture.w / (asset.w * scale)));
    const h = Math.min(1, Math.max(subject.h, picture.h / (asset.h * scale)));
    rect = {
      x: clamp(subject.x + subject.w / 2 - w / 2, 0, 1 - w),
      y: clamp(subject.y + subject.h / 2 - h / 2, 0, 1 - h),
      w,
      h,
    };
  }
  return [establish, { reason: 'target', rect }];
}

export type CaptionPlace = 'top' | 'bottom';

/**
 * Split a portrait viewport into the picture and the caption band. At the
 * bottom the band sits directly above the control bar; at the top it is the
 * top of the screen and the picture runs down to the bar. `captionH` is the
 * caption's measured height, clamped — except at exactly 0, which the
 * caller (`apps/player/src/story.ts` `Caption.heightFor`) sends for a step
 * with nothing to caption (no tooltip, no real name): no band is reserved at
 * all, and the picture takes the room a caption would otherwise have used,
 * rather than an empty band with nothing in it.
 */
export function storyFrame(
  viewport: Viewport,
  captionH: number,
  place: CaptionPlace,
): { picture: PxRect; caption: PxRect } {
  const h =
    captionH > 0 ? Math.round(clamp(captionH, CAPTION_MIN_PX, viewport.h * CAPTION_MAX_SHARE)) : 0;
  const picH = Math.max(1, viewport.h - h - STORY_CHROME_PX);
  if (place === 'top') {
    return {
      caption: { x: 0, y: 0, w: viewport.w, h },
      picture: { x: 0, y: h, w: viewport.w, h: picH },
    };
  }
  return {
    caption: { x: 0, y: picH, w: viewport.w, h },
    picture: { x: 0, y: 0, w: viewport.w, h: picH },
  };
}

/* ── story card ────────────────────────────────────────────────────────────
 * The phone story's look: a headline above a framed window, on the theme's
 * background. Geometry only — the player draws it in CSS, the exporter in
 * sharp — so both take their numbers from here (docs/plans/phone-story-cards.md). */

/** Two rings behind the window (accent tints), each this wide. */
export const CARD_RING_PX = 3;
/** Side margin between the phone's edge and the outer ring. */
export const CARD_MARGIN_PX = 16;
/**
 * A story is for a phone. A viewport this wide (a tablet, either way up) is
 * shown as the recording itself, horizontal, the way a desktop product is
 * seen on a tablet; the player does not tell the story there.
 */
export const STORY_MAX_WIDTH_PX = 600;
/** Window corner radius. */
export const CARD_RADIUS_PX = 14;
/** Air between the headline and the window's outer ring, and above the headline. */
export const CARD_GAP_PX = 12;
/** A still's window is this tall against its width; a scroll's takes all the room. */
export const CARD_ASPECT = 1.6;
/** A window fitted to its screen is never flatter than this against its width. */
export const CARD_SCREEN_MIN_ASPECT = 0.6;
/** Headline block: never shorter than this, never more than this share of the viewport. */
export const CARD_HEAD_MIN_PX = 64;
export const CARD_HEAD_MAX_SHARE = 0.3;
/** Share of the spare height that goes above the window (the rest below it). */
const CARD_SPARE_ABOVE = 0.4;

/** A story holds the whole screen this long, after pulling back to it, before the end card. */
export const STORY_END_HOLD_MS = 1400;

/** Generic names the pipeline gives a step it could not name say nothing to a viewer. */
const GENERIC_NAME = /^(screen|scroll|step) \d+$/i;

/**
 * What a story step's headline says: its tooltip, else a real name, else
 * nothing. Shared by the player's headline and the 9:16 export's, so the
 * video says what the phone says.
 */
export function captionText(step: Pick<Step, 'annotation' | 'name' | 'scroll'>): string {
  const tip = step.annotation?.text.trim();
  if (tip) return tip;
  // "Download PGN (2)": the pipeline's way of keeping two names apart, not words for a viewer.
  const name = step.name.trim().replace(/\s*\(\d+\)$/, '');
  // "Scroll mes": the pipeline's label for a scroll it read a word off, for
  // the editor. On a phone it would take a third of the screen to say nothing.
  if (step.scroll && /^scroll\b/i.test(name)) return '';
  return name && !GENERIC_NAME.test(name) ? name : '';
}

/**
 * The step shows only the whole screen: nothing to point at (no target, no
 * real focus) and no close-up of its own. Its window takes the screen's
 * shape (`cardFrame`'s `screen`).
 */
export function isWholeScreenStep(step: Step): boolean {
  if (step.scroll) return false;
  if (orderedShots(step).some((s) => s.reason === 'target')) return false;
  const focus = step.focus && !isFullFrame(step.focus) ? step.focus : null;
  return !storySubject(step) && !focus;
}

export interface CardFrame {
  /** The headline block, full width, top of the screen. */
  caption: PxRect;
  /** The window itself: the picture every layout is laid out in. */
  picture: PxRect;
  /** The window's corner radius. */
  radius: number;
  /** Width of each of the two rings behind the window. */
  ring: number;
}

/**
 * Split a portrait viewport into the headline and the framed window.
 * `captionH` is the measured headline height (0: a step with nothing to say
 * reserves none). `tall` is a scroll step, whose window takes all the room
 * to show as much of the page as it can; a still's window is squarer, so a
 * legible close-up fills it.
 */
export function cardFrame(
  viewport: Viewport,
  captionH: number,
  tall: boolean,
  /** Height kept clear at the bottom; the player's control bar by default (a video has none). */
  chromePx: number = STORY_CHROME_PX,
  /**
   * The screen's own size, for a step that shows only the whole screen (see
   * `isWholeScreenStep`): the window is then the screen's own shape instead
   * of a tall one with the picture floating in empty bands. Never below
   * `CARD_SCREEN_MIN_ASPECT` of its width, nor taller than the room.
   */
  screen?: { w: number; h: number },
): CardFrame {
  const ring = CARD_RING_PX;
  const inset = CARD_MARGIN_PX + ring;
  const head =
    captionH > 0
      ? Math.round(clamp(captionH, CARD_HEAD_MIN_PX, viewport.h * CARD_HEAD_MAX_SHARE))
      : 0;
  const w = Math.max(1, viewport.w - inset * 2);
  const top = head > 0 ? head : CARD_GAP_PX;
  const room = Math.max(1, viewport.h - chromePx - top - CARD_GAP_PX - ring * 2);
  const screenH =
    screen && screen.w > 0 && screen.h > 0
      ? Math.max(Math.round(w * (screen.h / screen.w)), Math.round(w * CARD_SCREEN_MIN_ASPECT))
      : 0;
  const h = tall ? room : Math.min(room, screenH > 0 ? screenH : Math.round(w * CARD_ASPECT));
  // A window fitted to its screen leaves spare height. The headline travels
  // with the window (one block, a little above centre) instead of staying at
  // the top with a gap before the picture; a tall window has no spare to place.
  const fitted = !tall && screenH > 0;
  const shift = fitted ? Math.round((room - h) * CARD_SPARE_ABOVE) : 0;
  const y = fitted
    ? Math.round(top + ring + shift)
    : Math.round(top + ring + (room - h) * CARD_SPARE_ABOVE);
  return {
    caption: { x: 0, y: shift, w: viewport.w, h: head },
    picture: { x: inset, y, w, h },
    radius: CARD_RADIUS_PX,
    ring,
  };
}

/**
 * Where the caption goes, from where the target lands in the picture: its
 * centre as a share of the picture's height, laid out with the band at the
 * bottom. No target: at the bottom, above the controls, like subtitles.
 */
export function captionPlace(targetCentre: number | null): CaptionPlace {
  if (targetCentre === null || !Number.isFinite(targetCentre)) return 'bottom';
  return targetCentre < CAPTION_TOP_BELOW ? 'top' : 'bottom';
}

type ShotAsset = Pick<Asset, 'w' | 'h'> & {
  medianTextH?: number | undefined;
  ink?: string | undefined;
};

/* ── clean edges ────────────────────────────────────────────────────────────
 * A close-up narrower than the screen cuts it somewhere. Cut through a
 * panel and the window shows "ecord", "udness", "l the open shift": half
 * words at the frame, which read as a broken picture. A gutter — the space
 * between two panels, a panel's padding, the gap between a table's columns —
 * is a place a frame can sit without cutting a word, and so is the screen's
 * own edge. Gutters are found in the pixels at publish (`@mado/autofocus/
 * shots`), which snaps each target shot's sides into them; the player has no
 * pixels to look at and reads them back off the shots (`shotGutters`). The
 * 9:16 export, which has the pixels, finds them itself for a step without
 * shots. Either way the same pure rule below picks the framing. */

/** A vertical band, normalized across the asset, where a frame edge cuts no content. */
export interface Gutter {
  a: number;
  b: number;
  /**
   * 0 (absent): a clean cut. 1: a second-best one that crosses a little
   * content (about one line); taken only when no clean pair of gutters fits.
   */
  cost?: number;
}

/** Snapping may zoom out to this share of the zoom it started from… */
export const SNAP_MIN_ZOOM = 0.7;
/** …or in to this multiple of it… */
export const SNAP_MAX_ZOOM = 1.25;
/**
 * …and never below this text size (CSS px) unless the framing was already
 * below it (a target too wide for the window is fitted whole, `shotLayout`).
 * Under the phone story's own "about 10 px" line
 * (docs/plans/phone-story-cards.md §1), on purpose: a whole word at 8–9 px
 * reads, half a word at 11 px does not. Found on the gallery's Rotaloop
 * search panel, whose wide field already sat the close-up at 10.3 px and
 * whose title was cut to "l the open shift" one gutter away; publish shapes
 * shots a pixel above this (`@mado/autofocus/shots`), so the share page's
 * window, narrower than the reference phone's, still reaches them.
 */
export const SNAP_MIN_TEXT_PX = 8;

/**
 * The gutters a step's own shots carry: its close-up's left and right side.
 * A target shot made at publish has its sides snapped into the screen's
 * gutters, so on a phone of another width the window can still land on them.
 * Lead-in shots are not snapped (a content box's edge can sit on a panel's
 * first letters) and say nothing. Empty for a step without shots — the
 * screen's own edges are always clean.
 */
export function shotGutters(step: Pick<Step, 'shots' | 'scroll'>): Gutter[] {
  if (step.scroll || !step.shots?.length) return [];
  const out: Gutter[] = [];
  for (const s of step.shots) {
    if (s.reason !== 'target') continue;
    for (const x of [s.rect.x, s.rect.x + s.rect.w]) {
      if (x > 0.001 && x < 0.999) out.push({ a: x, b: x });
    }
  }
  return out;
}

/**
 * Move and resize the span `[l, l + w]` (normalized x) so both its sides land
 * in a gutter or on the screen's edge, its width within `[minW, maxW]`, with
 * `keep` (the target) inside it and `pad` of air on each side of the target.
 * Of the spans that do, the one closest in zoom to `w`, then in position.
 * Null when none does: the framing stays as it was.
 */
export function snapSpan(
  l: number,
  w: number,
  keep: { a: number; b: number } | null,
  gutters: readonly Gutter[],
  minW: number,
  maxW: number,
  pad = 0,
): { l: number; w: number } | null {
  if (!gutters.length || w <= 0) return null;
  const lefts: Gutter[] = [{ a: 0, b: 0 }, ...gutters];
  const rights: Gutter[] = [...gutters, { a: 1, b: 1 }];
  const centre = l + w / 2;
  // The target's air never asks for room past the screen's own edge.
  // A gutter within `pad` of the target already is that air: a button at the
  // very edge of its panel keeps the panel's side, instead of asking for room
  // beyond the gutter and losing the panel (its window then cut it mid-text).
  const k = keep && {
    a: gutters.some((g) => g.b <= keep.a && keep.a - g.b < pad)
      ? keep.a
      : Math.max(0, keep.a - pad),
    b: gutters.some((g) => g.a >= keep.b && g.a - keep.b < pad)
      ? keep.b
      : Math.min(1, keep.b + pad),
  };
  const kw = k ? k.b - k.a : 0;
  let best: { l: number; w: number; cost: number } | null = null;
  for (const L of lefts) {
    for (const R of rights) {
      if (R.b <= L.a) continue;
      const lo = Math.max(minW, R.a - L.b, kw);
      const hi = Math.min(maxW, R.b - L.a, 1);
      if (lo > hi + 1e-9) continue;
      const nw = clamp(w, lo, hi);
      let l0 = Math.max(L.a, R.a - nw, 0);
      let l1 = Math.min(L.b, R.b - nw, 1 - nw);
      if (k) {
        l0 = Math.max(l0, k.b - nw);
        l1 = Math.min(l1, k.a);
      }
      if (l0 > l1 + 1e-9) continue;
      const nl = clamp(centre - nw / 2, l0, Math.max(l0, l1));
      const cost =
        (L.cost ?? 0) +
        (R.cost ?? 0) +
        Math.abs(Math.log(nw / w)) +
        (0.5 * Math.abs(nl + nw / 2 - centre)) / w;
      if (!best || cost < best.cost) best = { l: nl, w: nw, cost };
    }
  }
  return best && { l: best.l, w: best.w };
}

/** A window this close to the screen's left edge (as a share of its own width) starts at it. */
export const LINE_START_SNAP = 0.3;
/** The least zoom (as a share of the shot's own) taken to bring a line's start into the window. */
export const LINE_START_ZOOM_OUT = 0.65;

/**
 * Frame one shot in the picture: the rect as large as the picture allows,
 * centred, never revealing empty space beside the asset where it overflows.
 * `legible` (a close-up) floors the zoom at readable text — on a narrower
 * phone than the shot was shaped for, its edges give way rather than its
 * text — and `keep` (the step's target) is then held inside the picture.
 * `gutters`: where a side of the window may cut the screen cleanly; a window
 * narrower than the screen is snapped into them (`snapSpan`) rather than
 * cutting a panel's text mid-word.
 */
export function shotLayout(
  picture: PxRect,
  asset: ShotAsset,
  rect: NormRect,
  opts: {
    legible?: boolean;
    keep?: NormRect | null;
    gutters?: readonly Gutter[];
    /**
     * The demo's one close-up zoom, as a multiple of the whole-screen fit
     * (`walkZoom`). A legible shot then takes exactly this zoom instead of
     * its own, so every step of the walk is seen at the same scale and the
     * camera only pans. A target too wide for it still gets the zoom that
     * fits it whole (below).
     */
    zoom?: number;
  } = {},
): Layout {
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  const fit = Math.min(picture.w / asset.w, picture.h / asset.h);
  let scale = Math.min(picture.w / (rect.w * asset.w), picture.h / (rect.h * asset.h));
  if (opts.legible) scale = Math.max(scale, MIN_TEXT_PX / textH);
  scale = clamp(scale, fit, Math.max(fit, SHOT_MAX_TEXT_PX / textH));
  if (opts.legible && opts.zoom && opts.zoom > 0) scale = Math.max(fit, fit * opts.zoom);
  // The target is what the step asks the viewer to tap: it is never cut by
  // the window's edge. A target wider (or taller) than the window at the
  // legible zoom — a full-width form field, a wide clip on a timeline —
  // gets the zoom that fits it whole, with `SHOT_LEAD_PX` of air on both
  // sides, even though its text is then below the legibility floor: a field
  // seen whole at small type reads as a field; one cut at the frame reads as
  // a broken picture with a focus ring running off it. Never below the whole
  // screen (`fit`).
  if (opts.keep) {
    const kw = opts.keep.w * asset.w;
    const kh = opts.keep.h * asset.h;
    const room = Math.min(
      kw > 0 ? (picture.w - 2 * SHOT_LEAD_PX) / kw : Infinity,
      kh > 0 ? (picture.h - 2 * SHOT_LEAD_PX) / kh : Infinity,
    );
    if (room > 0 && room < scale) scale = Math.max(fit, room);
  }

  let sw = asset.w * scale;
  let sh = asset.h * scale;
  // A block wider than the picture: start at its left edge, so lines begin
  // whole and only their far end runs off (centring cut both ends). The
  // target, if it sits in the cut part, is brought back by `keep` below.
  let tx =
    rect.w * sw > picture.w + 0.5
      ? clamp(SHOT_LEAD_PX - rect.x * sw, picture.w - sw, 0)
      : placeOnAxis(sw, picture.w, rect.x, rect.w);
  if (opts.keep) tx = keepOnAxis(tx, sw, picture.w, opts.keep.x, opts.keep.w);
  // The window's sides into the screen's gutters, when it cuts the screen.
  if (opts.gutters?.length && sw > picture.w + 0.5) {
    const w = picture.w / sw;
    const textFloor = Math.min(scale, Math.max(scale * SNAP_MIN_ZOOM, SNAP_MIN_TEXT_PX / textH));
    const maxScale = Math.max(scale, Math.min(scale * SNAP_MAX_ZOOM, SHOT_MAX_TEXT_PX / textH));
    const snap = snapSpan(
      -tx / sw,
      w,
      opts.keep ? { a: opts.keep.x, b: opts.keep.x + opts.keep.w } : null,
      opts.gutters,
      picture.w / (asset.w * maxScale),
      picture.w / (asset.w * Math.max(fit, textFloor)),
      SHOT_LEAD_PX / sw,
    );
    if (snap) {
      scale = picture.w / (snap.w * asset.w);
      sw = asset.w * scale;
      sh = asset.h * scale;
      tx = clamp(-snap.l * sw, picture.w - sw, 0);
    }
  }
  // A window that stops just short of the screen's left edge cuts every line there mid-word (a
  // terminal's prompt, a list's first column; 2026-11-03 review): when the edge is within
  // `LINE_START_SNAP` of the window's width and the target still fits, the window starts at it.
  // A target that would not fit then takes a little less zoom (at most `LINE_START_ZOOM_OUT`)
  // when the edge is within 0.6 of the window.
  if (sw > picture.w + 0.5 && tx < 0) {
    const ww = picture.w / sw;
    const left = -tx / sw;
    const need = opts.keep ? opts.keep.x + opts.keep.w + SHOT_LEAD_PX / sw : 0;
    if (left < ww * LINE_START_SNAP && need <= ww) tx = 0;
    else if (left < ww * 0.6 && need > ww && need <= ww / LINE_START_ZOOM_OUT) {
      scale = Math.max(fit, picture.w / (need * asset.w));
      sw = asset.w * scale;
      sh = asset.h * scale;
      tx = 0;
    }
  }
  let ty = placeOnAxis(sh, picture.h, rect.y, rect.h);
  if (opts.keep) ty = keepOnAxis(ty, sh, picture.h, opts.keep.y, opts.keep.h);
  return {
    mode: 'focus',
    scale,
    tx: picture.x + tx,
    ty: picture.y + ty,
    containScale: fit,
    textPx: textH * scale,
  };
}

/** Centre `[start, start+len]` of a `size`-long picture in `span`, no gaps where it overflows. */
function placeOnAxis(size: number, span: number, start: number, len: number): number {
  if (size <= span) return (span - size) / 2;
  return clamp(span / 2 - (start + len / 2) * size, span - size, 0);
}

/**
 * Nudge `t` so `[start, start+len]` is fully on screen, when it can be — with
 * up to `SHOT_LEAD_PX` of air from the edge, so the target's pulse ring and
 * focus ring are not cut by the window's frame.
 */
function keepOnAxis(t: number, size: number, span: number, start: number, len: number): number {
  if (size <= span) return t;
  const a = t + start * size;
  const b = a + len * size;
  if (b - a > span) return t;
  const pad = Math.min(SHOT_LEAD_PX, (span - (b - a)) / 2);
  let out = t;
  if (a < pad) out -= a - pad;
  else if (b > span - pad) out -= b - (span - pad);
  return clamp(out, span - size, 0);
}

/* ── clean frames ───────────────────────────────────────────────────────────
 * Every zoomed desktop or tablet window — a step's target framing
 * (`stepCamera`), a region or a cue's frame and the lean into a small mark
 * (`regionLayout`), the guided camera's end — passes through `cleanFrame`
 * last. A side, a top or a bottom may cut the screen only at a clean place:
 * the asset's own edge, a line the document says is clean (`CleanLines.x`:
 * the step's shot sides, snapped into the screen's gutters at publish), or,
 * on an axis with no such line, anywhere that runs through nothing the
 * document knows holds text (`CleanLines.rects`). A window that cannot is
 * moved, then widened (never narrowed), and at the last shown whole. The
 * same cut ("Duomenų analit", "ublish", "andidates") had been fixed three
 * times on three other paths (the film, the phone shots, the desktop cues);
 * this is the one step every desktop zoom now takes. Phone story frames
 * (`shotLayout`, `cueShot`) are left as they are. */

/** What a step's document knows about where a desktop window may cut its screen. */
export interface CleanLines {
  /**
   * Vertical lines (normalized x) a side may sit on: the step's shot sides, snapped into the
   * screen's gutters at publish (`@mado/autofocus/shots`). Given, a side cuts only there or at
   * the screen's edge; empty, anywhere clear of `rects`.
   */
  x: readonly Gutter[];
  /** Horizontal lines (normalized y) a top or bottom may sit on: the shots' tops and bottoms. */
  y: readonly Gutter[];
  /** What no edge may run through: the step's marks, targets and focus. */
  rects: readonly NormRect[];
}

/** Nothing known: a window is left where it is. */
export const NO_LINES: CleanLines = { x: [], y: [], rects: [] };

/** An asset as the clean frame reads it: its size, and its ink boxes when publish found them. */
export type CleanAsset = Pick<Asset, 'w' | 'h'> & { ink?: string | undefined };

const inkCache = new Map<string, readonly NormRect[] | null>();

/**
 * `Asset.ink` decoded (12 bits a side, 6 bytes a box; see the field), once per string; null when
 * it does not read.
 */
export function inkRects(ink: string | undefined): readonly NormRect[] | null {
  if (!ink) return null;
  const hit = inkCache.get(ink);
  if (hit !== undefined) return hit;
  let out: NormRect[] | null = null;
  try {
    const bin = atob(ink);
    if (bin.length % 6 === 0) {
      out = [];
      const k = 1 / 4095;
      for (let o = 0; o < bin.length; o += 6) {
        const c = (i: number): number => bin.charCodeAt(o + i);
        const x0 = (c(0) << 4) | (c(1) >> 4);
        const y0 = ((c(1) & 15) << 8) | c(2);
        const x1 = (c(3) << 4) | (c(4) >> 4);
        const y1 = ((c(4) & 15) << 8) | c(5);
        if (x1 > x0 && y1 > y0)
          out.push({ x: x0 * k, y: y0 * k, w: (x1 - x0) * k, h: (y1 - y0) * k });
      }
    }
  } catch {
    out = null;
  }
  if (inkCache.size > 256) inkCache.clear();
  inkCache.set(ink, out);
  return out;
}

/** A step's clean lines: its shots' sides, and its cues, hotspots and focus as rects not to cut. */
export function stepLines(
  step: Pick<Step, 'shots' | 'scroll' | 'focus'> & {
    cues?: Step['cues'];
    hotspots?: readonly Pick<Hotspot, 'geom'>[];
  },
): CleanLines {
  const y: Gutter[] = [];
  if (!step.scroll) {
    for (const sh of step.shots ?? []) {
      for (const v of [sh.rect.y, sh.rect.y + sh.rect.h]) {
        if (v > 0.001 && v < 0.999) y.push({ a: v, b: v });
      }
    }
  }
  const rects: NormRect[] = [
    ...(step.cues ?? []).map((c) => c.rect),
    ...(step.hotspots ?? []).map((h) => h.geom),
  ];
  if (step.focus && !isFullFrame(step.focus)) rects.push(step.focus);
  return { x: shotSides(step), y, rects };
}

/** Each zoom tried on the way out is this much wider than the last. */
const CLEAN_WIDEN_STEP = 0.98;
/** A rect this close to an edge (asset px) touches it rather than being cut by it. */
const CLEAN_TOL_PX = 1;

/**
 * Where an edge may cut one axis (`along`: 'x' for the sides), for a window spanning
 * `[o0, o1]` on the other axis: sorted bands, the asset's two edges always among them.
 */
function cleanBands(
  lines: CleanLines,
  along: 'x' | 'y',
  o0: number,
  o1: number,
  tol: number,
  otherTol: number,
  ink: readonly NormRect[] | null = null,
): Gutter[] {
  // The interiors of the rects that reach into the window's span on the other axis.
  const blocks: [number, number][] = [];
  const block = (r: NormRect): void => {
    const [p0, p1, q0, q1] =
      along === 'x' ? [r.x, r.x + r.w, r.y, r.y + r.h] : [r.y, r.y + r.h, r.x, r.x + r.w];
    if (Math.min(q1, o1) - Math.max(q0, o0) <= otherTol) return;
    if (p1 - p0 <= 2 * tol) return;
    blocks.push([p0 + tol, p1 - tol]);
  };
  // The picture's own ink, when publish found it, says where the words are, row by row; the
  // document's marks are only a stand-in for it (a mark round a panel says nothing of the gaps
  // between its rows).
  (ink ?? lines.rects).forEach(block);
  blocks.sort((a, b) => a[0] - b[0]);
  const edges: Gutter[] = [
    { a: 0, b: 0 },
    { a: 1, b: 1 },
  ];
  // With the picture's own ink known, a side cuts anywhere clear of it. Without, the shots'
  // sides are the only lines known to be clean: a side cuts there or nowhere.
  if (!ink && along === 'x' && lines.x.length) {
    const inside = (v: number): boolean => blocks.some(([a, b]) => v > a && v < b);
    const out = lines.x.filter((g) => !inside(g.a) && !inside(g.b));
    return [...edges, ...out].sort((p, q) => p.a - q.a);
  }
  const out: Gutter[] = [];
  let at = 0;
  for (const [a, b] of blocks) {
    if (a > at) out.push({ a: at, b: a });
    at = Math.max(at, b);
  }
  if (at < 1) out.push({ a: at, b: 1 });
  return [...edges, ...out].sort((p, q) => p.a - q.a);
}

/**
 * Where a span `len` long may start so both its ends land in `bands` and `keep` stays inside it:
 * for each pair of bands that can hold its two ends, the start closest to `pref`. Closest first.
 */
function spanStarts(
  len: number,
  bands: readonly Gutter[],
  keep: { a: number; b: number } | null,
  pref: number,
): number[] {
  let lo0 = 0;
  let hi0 = 1 - len;
  if (keep) {
    lo0 = Math.max(lo0, keep.b - len);
    hi0 = Math.min(hi0, keep.a);
  }
  if (lo0 > hi0 + 1e-9) return [];
  const out: { t: number; cost: number }[] = [];
  for (const I of bands) {
    if (I.b < lo0 - 1e-9 || I.a > hi0 + 1e-9) continue;
    for (const J of bands) {
      const lo = Math.max(lo0, I.a, J.a - len);
      const hi = Math.min(hi0, I.b, J.b - len);
      if (lo > hi + 1e-9) continue;
      const t = clamp(pref, lo, Math.max(lo, hi));
      out.push({ t, cost: Math.abs(t - pref) + (I.cost ?? 0) + (J.cost ?? 0) });
    }
  }
  return out.sort((p, q) => p.cost - q.cost).map((o) => o.t);
}

/** `spanStarts`' first: the start closest to `pref`, or null. */
function fitSpan(
  len: number,
  bands: readonly Gutter[],
  keep: { a: number; b: number } | null,
  pref: number,
): number | null {
  return spanStarts(len, bands, keep, pref)[0] ?? null;
}

function intersectRect(a: NormRect, b: NormRect): NormRect | null {
  const x0 = Math.max(a.x, b.x);
  const y0 = Math.max(a.y, b.y);
  const x1 = Math.min(a.x + a.w, b.x + b.w);
  const y1 = Math.min(a.y + a.h, b.y + b.h);
  return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/** Does an edge at `v` sit in `bands`? */
const inBands = (v: number, bands: readonly Gutter[], eps: number): boolean =>
  bands.some((g) => v >= g.a - eps && v <= g.b + eps);

/**
 * `l` with every edge of its window on a clean place (`CleanLines`): unchanged when it already
 * is, else moved, else widened step by step (never closer than `l`), keeping `keep` (with its
 * air) in view. When no zoom at or above `floor`'s by `CAMERA_MIN_ZOOM` is clean, `floor` (the
 * whole screen on a desktop).
 */
export function cleanFrame(
  l: Layout,
  viewport: Viewport,
  asset: CleanAsset,
  lines: CleanLines,
  opts: { floor: Layout; keep?: NormRect | null; padPx?: number },
): Layout {
  const ink = inkRects(asset.ink);
  if (!ink && !lines.x.length && !lines.rects.length) return l;
  const s0 = l.scale;
  const minS = Math.max(opts.floor.scale, opts.floor.containScale * CAMERA_MIN_ZOOM);
  if (s0 <= opts.floor.scale + 1e-9) return l;
  const tolX = CLEAN_TOL_PX / asset.w;
  const tolY = CLEAN_TOL_PX / asset.h;
  const sizeAt = (s: number) => ({ w: viewport.w / (asset.w * s), h: viewport.h / (asset.h * s) });
  const cx = (viewport.w / 2 - l.tx) / (asset.w * s0);
  const cy = (viewport.h / 2 - l.ty) / (asset.h * s0);
  // What the window shows of `keep` now is what it keeps showing (a field wider than the window,
  // framed from its start, keeps its start).
  const seen0 = {
    x: -l.tx / (asset.w * s0),
    y: -l.ty / (asset.h * s0),
    w: viewport.w / (asset.w * s0),
    h: viewport.h / (asset.h * s0),
  };
  const kept = opts.keep ? intersectRect(opts.keep, seen0) : null;
  const keepAt = (s: number, along: 'x' | 'y'): { a: number; b: number } | null => {
    const k = kept;
    if (!k) return null;
    const pad = (opts.padPx ?? 0) / (s * (along === 'x' ? asset.w : asset.h));
    const [a, b] = along === 'x' ? [k.x, k.x + k.w] : [k.y, k.y + k.h];
    return { a: Math.max(0, a - pad), b: Math.min(1, b + pad) };
  };
  const span = (t: number | null, len: number): [number, number] =>
    len >= 1 || t === null ? [0, 1] : [t, t + len];

  // Already clean: as it is, to the last bit.
  {
    const { w, h } = sizeAt(s0);
    const x0 = -l.tx / (asset.w * s0);
    const y0 = -l.ty / (asset.h * s0);
    const xs = span(w >= 1 ? null : x0, w);
    const ys = span(h >= 1 ? null : y0, h);
    const xb = cleanBands(lines, 'x', ys[0], ys[1], tolX, tolY, ink);
    const yb = cleanBands(lines, 'y', xs[0], xs[1], tolY, tolX, ink);
    const clean =
      (w >= 1 || (inBands(xs[0], xb, 1e-6) && inBands(xs[1], xb, 1e-6))) &&
      (h >= 1 || (inBands(ys[0], yb, 1e-6) && inBands(ys[1], yb, 1e-6)));
    if (clean) return l;
  }

  /** How many tops (or lefts) are tried at one zoom before it is given up. */
  const TRIES = 24;
  const at = (s: number, use: CleanLines): Layout | null => {
    const { w, h } = sizeAt(s);
    const kx = keepAt(s, 'x');
    const ky = keepAt(s, 'y');
    const sideAt = (ys: [number, number]): number | null =>
      w >= 1
        ? null
        : fitSpan(w, cleanBands(use, 'x', ys[0], ys[1], tolX, tolY, ink), kx, cx - w / 2);
    // The rows decide where a side may cut, and the columns where a top may: each axis depends
    // on the other. Tops are tried from those clean across the narrowest the window could be
    // (its keep, else its centre), closest to where it was first; each with its best sides,
    // then checked across the columns it ended on.
    let tops: (number | null)[] = [null];
    if (h < 1) {
      const narrow: [number, number] = kx
        ? [kx.a, kx.b]
        : [clamp(cx, 0, 1) - 1e-3, clamp(cx, 0, 1) + 1e-3];
      tops = spanStarts(
        h,
        cleanBands(use, 'y', narrow[0], narrow[1], tolY, tolX, ink),
        ky,
        cy - h / 2,
      ).slice(0, TRIES);
    }
    for (const ty of tops) {
      const ys = span(ty, h);
      const tx = sideAt(ys);
      if (w < 1 && tx === null) continue;
      const xs = span(tx, w);
      if (ty !== null) {
        const yb = cleanBands(use, 'y', xs[0], xs[1], tolY, tolX, ink);
        if (!inBands(ty, yb, 1e-6) || !inBands(ty + h, yb, 1e-6)) continue;
      }
      const sw = asset.w * s;
      const sh = asset.h * s;
      return {
        mode: 'focus',
        scale: s,
        tx: tx === null ? (viewport.w - sw) / 2 : -tx * sw,
        ty: ty === null ? (viewport.h - sh) / 2 : -ty * sh,
        containScale: l.containScale,
        textPx: (l.textPx * s) / s0,
      };
    }
    return null;
  };

  // The zooms to try, closest first: steps on the way out, and every width two named lines
  // (or a named line and the screen's edge) make exactly.
  const scales = new Set<number>([s0]);
  for (let s = s0 * CLEAN_WIDEN_STEP; s > minS; s *= CLEAN_WIDEN_STEP) scales.add(s);
  if (minS <= s0) scales.add(minS);
  if (!ink && lines.x.length) {
    const pts = [0, 1, ...lines.x.flatMap((g) => [g.a, g.b])];
    for (const a of pts) {
      for (const b of pts) {
        if (b <= a) continue;
        const s = viewport.w / ((b - a) * asset.w);
        if (s <= s0 && s >= minS) scales.add(s);
      }
    }
  }
  const order = [...scales].sort((p, q) => q - p);
  for (const s of order) {
    const out = at(s, lines);
    if (out) return out;
  }
  // No zoom puts the sides on the shots' lines: then sides clear of every rect the document
  // knows, before the whole screen. A published demo without ink keeps a camera that moves.
  if (!ink && lines.x.length) {
    const loose: CleanLines = { ...lines, x: [] };
    for (const s of order) {
      const out = at(s, loose);
      if (out) return out;
    }
  }
  return opts.floor;
}

/**
 * The whole screen in the picture: the zoomed-out framing a story opens and
 * closes on. `frame` is what counts as "the whole screen": the page's content
 * box (`overviewFrame`), so a capture with wide empty margins is not shown as
 * a small picture in a large dark window. Absent: the full capture.
 */
export function overviewLayout(
  picture: PxRect,
  asset: ShotAsset,
  frame: NormRect = FULL_FRAME,
): Layout {
  return shotLayout(picture, asset, frame);
}

/** A content box smaller than this share of the capture's width or height is worth framing to. */
const OVERVIEW_FRAME_MAX_SHARE = 0.9;

/**
 * The page's content box, for the whole-screen views of a phone story: the
 * union of the `establish` shots publish found (`@mado/autofocus/shots`: the
 * screen's content with its uniform margins and browser/OS chrome cut off).
 * A document made before shots, or one whose content fills the capture, gets
 * the full frame: nothing changes for it. Pure.
 */
export function overviewFrame(doc: { steps: readonly Step[] }): NormRect {
  let x0 = 1;
  let y0 = 1;
  let x1 = 0;
  let y1 = 0;
  let found = false;
  for (const step of doc.steps) {
    if (step.scroll) continue;
    for (const shot of step.shots ?? []) {
      if (shot.reason !== 'establish') continue;
      found = true;
      x0 = Math.min(x0, shot.rect.x);
      y0 = Math.min(y0, shot.rect.y);
      x1 = Math.max(x1, shot.rect.x + shot.rect.w);
      y1 = Math.max(y1, shot.rect.y + shot.rect.h);
    }
  }
  if (!found || x1 <= x0 || y1 <= y0) return FULL_FRAME;
  const frame = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  // Only worth it when a real share of the capture is cut away.
  return frame.w > OVERVIEW_FRAME_MAX_SHARE && frame.h > OVERVIEW_FRAME_MAX_SHARE
    ? FULL_FRAME
    : frame;
}

/* ── phone walkthrough ──────────────────────────────────────────────────────
 * How the player tells a desktop capture on a phone (docs/plans/phone-walkthrough.md):
 * the picture is the phone's whole screen above the control bar — no card, no
 * headline band, the tip is a bubble beside the target — and the camera holds
 * one zoom for the whole demo, panning from target to target. The whole
 * screen is shown where the demo opens and ends, and on demand (a pinch). */

/** The product's corner radius in CSS px when its site was not read (`theme.radius` absent). */
export const PRODUCT_RADIUS_DEFAULT = 6;

/**
 * The product's own corner radius, in px of a screenshot `asset`, for every frame drawn round a
 * part of it: a highlight's hole and ring, a target's pulse, a showcase's panel (founder,
 * 2026-10-21: "if it's rounded, rounded; if it's squared, squared"). `theme.radius` is read off
 * its site in CSS px; a screenshot wider than a 1440 px screen comes from a denser one.
 */
export function productRadiusPx(
  theme: { radius?: number | undefined },
  asset: { w: number },
): number {
  return (theme.radius ?? PRODUCT_RADIUS_DEFAULT) * Math.max(1, asset.w / 1440);
}

/** The picture of a walkthrough: the viewport, less the control bar at the bottom. */
export function walkFrame(viewport: Viewport, chromePx: number = STORY_CHROME_PX): PxRect {
  return { x: 0, y: 0, w: viewport.w, h: Math.max(1, viewport.h - chromePx) };
}

/**
 * Keep a dragged framing on the screen: where the picture is wider (taller)
 * than the window it never shows empty space past its edge; where it is
 * narrower it stays centred. The viewer looking around a step (a drag).
 */
export function lookLayout(layout: Layout, picture: PxRect, asset: ShotAsset): Layout {
  const sw = asset.w * layout.scale;
  const sh = asset.h * layout.scale;
  const tx =
    sw <= picture.w
      ? picture.x + (picture.w - sw) / 2
      : clamp(layout.tx, picture.x + picture.w - sw, picture.x);
  const ty =
    sh <= picture.h
      ? picture.y + (picture.h - sh) / 2
      : clamp(layout.ty, picture.y + picture.h - sh, picture.y);
  return { ...layout, tx, ty };
}

/**
 * A screen at the walkthrough's zoom, centred: where a step with nothing to
 * point at opens when the camera cannot carry on from the step before (the
 * viewer can look around, or pinch for all of it).
 */
export function zoomLayout(picture: PxRect, asset: ShotAsset, zoom: number): Layout {
  const whole = overviewLayout(picture, asset);
  const scale = whole.containScale * Math.max(1, zoom);
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  return lookLayout(
    {
      ...whole,
      scale,
      tx: picture.x + picture.w / 2 - (asset.w * scale) / 2,
      ty: picture.y + picture.h / 2 - (asset.h * scale) / 2,
      textPx: textH * scale,
    },
    picture,
    asset,
  );
}

/**
 * The demo's one close-up zoom, as a multiple of the whole-screen fit: the
 * median of what each target step would zoom to on its own to make its text
 * legible (`fallbackShots` + `shotLayout`). One scale for every step makes the
 * walk a pan, never a zoom out and back in. 1 when no step has a target.
 */
export function walkZoom(
  doc: { steps: Step[]; assets: Record<string, ShotAsset> },
  picture: PxRect,
): number {
  const zooms: number[] = [];
  for (const step of doc.steps) {
    if (step.scroll) continue;
    const asset = doc.assets[step.asset];
    if (!asset) continue;
    const subject = storySubject(step);
    if (!subject) continue;
    const close = fallbackShots(step, asset, picture).at(-1)!;
    if (close.reason !== 'target') continue;
    const fit = Math.min(picture.w / asset.w, picture.h / asset.h);
    const own = shotLayout(picture, asset, close.rect, { legible: true, keep: subject });
    if (fit > 0) zooms.push(own.scale / fit);
  }
  if (!zooms.length) return 1;
  zooms.sort((a, b) => a - b);
  const mid = zooms.length >> 1;
  const median = zooms.length % 2 ? zooms[mid]! : (zooms[mid - 1]! + zooms[mid]!) / 2;
  return Math.max(1, median);
}

/* ── phone overview ─────────────────────────────────────────────────────────
 * Orient first, then point (docs/plans/phone-overview.md). A step about the
 * page shows the page; a step about a control opens on the whole screen when
 * the screen is new, holds, then glides to it — at a pace a small screen can
 * follow. Shared by the player's walkthrough and the 9:16 export's story. */

export type StepView = 'overview' | 'action';

/**
 * A target this tall a share of the screen is a part of the page (a panel, a
 * card, a list), not a control. Width alone says nothing: a modal's primary
 * button or a search field can be a third of a desktop screen wide and is
 * still one thing to press.
 */
export const OVERVIEW_TARGET_H = 0.3;
/** …and one covering this share of the screen's area (a chart, a card grid) is too. */
export const OVERVIEW_TARGET_AREA = 0.05;

/**
 * What a step is about on a phone: `Step.view` when the document says, else
 * derived — nothing the camera trusts to point at, or a target the size of a
 * part of the page, is an overview; a step that types into its target, and
 * any other target, is an action. Words are not read here (the director
 * reads them and writes `view`). A scroll step is neither; it says `action`.
 */
export function stepView(
  step: Pick<Step, 'view' | 'scroll' | 'input' | 'hotspots' | 'annotation' | 'focus' | 'shots'>,
): StepView {
  if (step.view === 'overview' || step.view === 'action') return step.view;
  if (step.scroll) return 'action';
  if (step.input) return 'action';
  // Nothing to press (a screen to look at, a tooltip pinned to a chart):
  // the step is about what is on the page.
  if (!step.hotspots.length) return 'overview';
  const subject = storySubject(step as Step);
  if (!subject) return 'overview';
  if (subject.h >= OVERVIEW_TARGET_H || subject.w * subject.h >= OVERVIEW_TARGET_AREA) {
    return 'overview';
  }
  return 'action';
}

/** A shot rect this close to the whole screen says nothing about where to look. */
const NEAR_WHOLE = 0.9;
function nearWhole(r: NormRect): boolean {
  return r.w >= NEAR_WHOLE && r.h >= NEAR_WHOLE;
}

/**
 * Is `to` on the screen the viewer was already looking at, arriving from
 * `from`? The same picture, or a picture of the same size that publish-time
 * auto focus found changed only in part (its `result` shot: a dropdown, a
 * panel). Anything else — another page, a dialog over a scrim, after a
 * scroll — is a new screen, and is shown whole first. No pixels at runtime;
 * the exporter, which has them, measures this itself (`render.ts`).
 */
export function sameScreen(
  from: Pick<Step, 'asset' | 'scroll'> | null | undefined,
  to: Pick<Step, 'asset' | 'scroll' | 'shots'>,
  assets: Record<string, Pick<Asset, 'w' | 'h'>>,
): boolean {
  if (!from || from.scroll || to.scroll) return false;
  if (from.asset === to.asset) return true;
  const a = assets[from.asset];
  const b = assets[to.asset];
  if (!a || !b || a.w !== b.w || a.h !== b.h) return false;
  return !!to.shots?.some((s) => s.reason === 'result');
}

/**
 * Click-through: how long a new screen is shown whole before the camera moves
 * to its target. Long enough to take in the page's shape (a sidebar, a
 * header, where a panel opened); short enough that a viewer who tapped is
 * not kept waiting. A tap during it skips to the target.
 */
export const ESTABLISH_HOLD_MS = 1200;
/**
 * Watch mode: the hold is this share of the step's narration, so its first
 * clause ("This is the editor…", "Start by saying…") is heard over the page
 * and the move to the control comes as the sentence reaches it. Without word
 * timings this is the honest proxy: narration leads with where we are.
 */
export const WATCH_LEAD_SHARE = 0.4;
/** Bounds on the Watch hold: a little over click-through's at least… */
export const WATCH_HOLD_MIN_MS = 1600;
/** …and never so long that a long narration's close-up is cut short. */
export const WATCH_HOLD_MAX_MS = 3600;

/** How long Watch mode (and the video that records it) holds a new screen whole. */
export function watchEstablishHold(narrationMs: number | null | undefined): number {
  if (!narrationMs || narrationMs <= 0) return WATCH_HOLD_MIN_MS;
  return Math.round(clamp(narrationMs * WATCH_LEAD_SHARE, WATCH_HOLD_MIN_MS, WATCH_HOLD_MAX_MS));
}

/**
 * Two screens dissolve over this on a phone (the old close-up into the new
 * whole screen): no zoom during it, so nothing snaps; the camera moves only
 * once the new screen is on the glass.
 */
export const WALK_SCREEN_FADE_MS = 420;
/**
 * No camera move on a phone is faster than this. The desktop pan's floor
 * (`CAMERA_PAN_MIN_MS`, 420) measured as a 1.8× zoom change in 400 ms on a
 * 358 px picture: the eye loses the page it was on.
 */
export const WALK_MOVE_MIN_MS = 700;
/** …nor slower than this: a 5× zoom from the whole screen to a button. */
export const WALK_MOVE_MAX_MS = 1600;
/** A move's time: a base, plus per e-fold of zoom change, plus per px the picture travels. */
export const WALK_MOVE_BASE_MS = 500;
export const WALK_MOVE_MS_PER_ZOOM = 520;
export const WALK_MOVE_MS_PER_PX = 0.5;

/**
 * How long a phone camera move takes: grows with how much the zoom changes
 * (a 5× zoom-in from the whole screen ≈ 1.4 s) and how far the picture
 * travels, within `WALK_MOVE_MIN_MS`..`WALK_MOVE_MAX_MS`.
 */
export function walkMoveDuration(from: Layout, to: Layout, viewport: Viewport): number {
  const cx = viewport.w / 2;
  const cy = viewport.h / 2;
  const ax = (cx - from.tx) / from.scale;
  const ay = (cy - from.ty) / from.scale;
  const bx = (cx - to.tx) / to.scale;
  const by = (cy - to.ty) / to.scale;
  const travel = Math.hypot(ax - bx, ay - by) * Math.min(from.scale, to.scale);
  const zoom = Math.abs(Math.log(to.scale / from.scale));
  return clamp(
    Math.round(WALK_MOVE_BASE_MS + zoom * WALK_MOVE_MS_PER_ZOOM + travel * WALK_MOVE_MS_PER_PX),
    WALK_MOVE_MIN_MS,
    WALK_MOVE_MAX_MS,
  );
}

/**
 * A step's shots in playing order: lead-ins (a `result`, an `establish`) in
 * the order written, the `target` last — its framing is where the hotspots
 * are live. A repeated reason keeps its first shot.
 */
export function orderedShots(step: Pick<Step, 'shots' | 'scroll'>): Shot[] {
  if (step.scroll || !step.shots?.length) return [];
  const seen = new Set<ShotReason>();
  const lead: Shot[] = [];
  let target: Shot | null = null;
  for (const s of step.shots) {
    if (seen.has(s.reason)) continue;
    seen.add(s.reason);
    if (s.reason === 'target') target = s;
    else lead.push(s);
  }
  return target ? [...lead, target] : lead;
}

export interface StoryBeat {
  reason: ShotReason;
  layout: Layout;
  /** How long the camera rests here before moving on (0 on the last beat). */
  holdMs: number;
}

/**
 * The camera's walk through a step: one beat per shot, in order. The last
 * beat is where the step rests and its hotspots are live. A step without
 * shots in a story document walks `fallbackShots`; a scroll step (which
 * never has shots) gets one beat, its ordinary framing inside the picture —
 * the stage frames it with `scrollLayout` anyway. Consecutive beats that frame the same pixels are one.
 */
export function storyBeats(
  step: Step,
  picture: PxRect,
  asset: ShotAsset,
  motion?: Motion,
  opts: {
    /** Open on the whole screen, whatever the step's own first shot is. */
    overview?: boolean;
    /**
     * False: the step is on the screen the viewer was already looking at, so
     * it goes straight to its target — no whole screen or lead-in in front
     * (the 9:16 cut, which establishes a screen only when it changes).
     * `overview` still wins. Absent: every lead-in plays, as before.
     */
    lead?: boolean;
    /** Where the window may cut the screen; the step's own shots' sides by default (`shotGutters`). */
    gutters?: readonly Gutter[];
    /** The demo's one close-up zoom (`walkZoom`); absent: each close-up its own legible zoom. */
    zoom?: number;
    /**
     * The step arrives on a new screen (`sameScreen`): open on the whole
     * screen, hold `ESTABLISH_HOLD_MS`, then move to the target
     * (docs/plans/phone-overview.md §2.2). `overview` (the demo's opening)
     * wins with its own longer hold.
     */
    establish?: boolean;
    /** Frame the step as this, whatever it is (`stepView`) — a spread on an overview step. */
    view?: StepView;
    /** What the whole screen is (`overviewFrame`): the page's content box. Absent: the full capture. */
    frame?: NormRect;
  } = {},
): StoryBeat[] {
  // A step about the page shows the page (§2.1). On a phone a whole desktop screen is text a
  // couple of pixels high, so when publish found where to read (the step's own shots: what it
  // opened, its text), the page is shown first and the camera then walks there and rests
  // (2026-10-09, Talentika's Actions menu was never readable). No such shots: the page, and
  // it stays.
  if (!step.scroll && (opts.view ?? stepView(step)) === 'overview') {
    const page = overviewLayout(picture, asset, opts.frame);
    const walk = orderedShots(step).filter((sh) => !nearWhole(sh.rect));
    // Nothing to press, but the line points at a place on the screen (its anchor or focus): on a
    // phone the whole desktop is unreadable, so the page is shown, then the camera walks to what
    // the line is about, legible, and rests (2026-11-03 review: "Episode 42 is out: 48 minutes"
    // over a whole desktop frame on every last step). The author's own "overview" is kept.
    if (!walk.length && !step.hotspots.length && step.view !== 'overview') {
      const close = fallbackShots(step, asset, picture).at(-1)!;
      if (close.reason === 'target' && !nearWhole(close.rect)) walk.push(close);
    }
    if (!walk.length) return [{ reason: 'target', layout: page, holdMs: 0 }];
    const gutters = opts.gutters ?? shotGutters(step);
    const beats: StoryBeat[] = [{ reason: 'establish', layout: page, holdMs: ESTABLISH_HOLD_MS }];
    walk.forEach((sh, i) => {
      // Legible: these shots exist to be read; one too wide for that is read from its start.
      const layout = shotLayout(picture, asset, sh.rect, { gutters, legible: true });
      const last = i === walk.length - 1;
      const prev = beats[beats.length - 1]!;
      if (nearLayout(prev.layout, layout, picture)) {
        if (last) prev.holdMs = 0;
        return;
      }
      beats.push({
        reason: sh.reason,
        layout,
        holdMs: last ? 0 : (sh.holdMs ?? SHOT_HOLD_MS[sh.reason]),
      });
    });
    beats[beats.length - 1]!.holdMs = 0;
    return beats;
  }
  const own = orderedShots(step);
  let shots = own.length ? own : fallbackShots(step, asset, picture);
  // A close-up drawn round the whole screen (the author's "Show whole
  // screen", saved as the step's only shot) says nothing about where to look:
  // the close-up is the one a step without shots gets, round its target, so
  // the target is in the middle of the window, not pushed against its edge.
  const subjectRect = storySubject(step);
  if (subjectRect) {
    const close = shots.findIndex((s) => s.reason === 'target' && nearWhole(s.rect));
    if (close >= 0) {
      const fallback = fallbackShots(step, asset, picture).at(-1)!;
      if (fallback.reason === 'target') {
        shots = shots.map((s, i) => (i === close ? { ...s, rect: fallback.rect } : s));
      }
    }
  }
  if (opts.lead === false && shots.some((s) => s.reason === 'target')) {
    shots = shots.filter((s) => s.reason === 'target');
  }
  const gutters = opts.gutters ?? shotGutters(step);
  if ((opts.overview || opts.establish) && shots.length && !step.scroll) {
    const holdMs = opts.overview ? OVERVIEW_HOLD_MS : ESTABLISH_HOLD_MS;
    const lead: Shot = { reason: 'establish', rect: FULL_FRAME, holdMs };
    shots = shots[0]!.reason === 'establish' ? [lead, ...shots.slice(1)] : [lead, ...shots];
  }
  if (!shots.length) {
    const inner = stepCamera(step, {
      viewport: { w: picture.w, h: picture.h },
      asset,
      allowFocus: true,
      motion,
      clean: false,
    });
    const layout = { ...inner, tx: inner.tx + picture.x, ty: inner.ty + picture.y };
    return [{ reason: 'target', layout, holdMs: 0 }];
  }
  const subject = storySubject(step);
  const beats: StoryBeat[] = [];
  shots.forEach((shot, i) => {
    const last = i === shots.length - 1;
    const target = shot.reason === 'target';
    const layout = shotLayout(picture, asset, shot.rect, {
      legible: target,
      keep: target ? subject : null,
      gutters,
      ...(opts.zoom ? { zoom: opts.zoom } : {}),
    });
    // A target at the far end of a block wider than the picture: first read
    // the block from its left edge, then pan across to the target, instead
    // of only ever showing the cut-off end. Not in the walkthrough (`zoom`):
    // the whole screen has already been shown, and at the walk's zoom the
    // left edge of a wide block is an empty corner (phone-overview.md §1).
    if (target && subject && !opts.zoom) {
      const start = shotLayout(picture, asset, shot.rect, {
        legible: true,
        keep: null,
        gutters,
        ...(opts.zoom ? { zoom: opts.zoom } : {}),
      });
      const before = beats[beats.length - 1];
      if (
        Math.abs(start.tx - layout.tx) > READ_PAN_MIN_PX &&
        start.scale === layout.scale &&
        !(before && nearLayout(before.layout, start, picture))
      ) {
        beats.push({ reason: 'establish', layout: start, holdMs: READ_HOLD_MS });
      }
    }
    const prev = beats[beats.length - 1];
    if (prev && nearLayout(prev.layout, layout, picture)) {
      prev.reason = shot.reason;
      if (last) prev.holdMs = 0;
      return;
    }
    beats.push({
      reason: shot.reason,
      layout,
      holdMs: last ? 0 : (shot.holdMs ?? SHOT_HOLD_MS[shot.reason]),
    });
  });
  return beats;
}

/**
 * A lead-in this close to the next framing is not worth a move: the same
 * zoom within `SHOT_NEAR_ZOOM` and the picture's centre within
 * `SHOT_NEAR_SHIFT` of the picture's short side. A nudge reads as a jitter,
 * not as a camera deciding to look somewhere.
 */
export const SHOT_NEAR_ZOOM = 0.12;
export const SHOT_NEAR_SHIFT = 0.08;

function nearLayout(a: Layout, b: Layout, picture: PxRect): boolean {
  if (Math.abs(Math.log(a.scale / b.scale)) >= SHOT_NEAR_ZOOM) return false;
  const cx = picture.x + picture.w / 2;
  const cy = picture.y + picture.h / 2;
  // The asset point under the picture's centre, in b's pixels.
  const dx = ((cx - a.tx) / a.scale - (cx - b.tx) / b.scale) * b.scale;
  const dy = ((cy - a.ty) / a.scale - (cy - b.ty) / b.scale) * b.scale;
  return Math.hypot(dx, dy) < SHOT_NEAR_SHIFT * Math.min(picture.w, picture.h);
}

/** How long a beat sequence takes once the step has arrived, pans included. */
export function storyDuration(beats: StoryBeat[], viewport: Viewport): number {
  let ms = 0;
  for (let i = 1; i < beats.length; i++) {
    const a = beats[i - 1]!;
    ms += a.holdMs + panDuration(a.layout, beats[i]!.layout, viewport);
  }
  return ms;
}

/* ── desktop guided camera ──────────────────────────────────────────────────
 *
 * docs/plans/desktop-guided-camera.md. On a desktop or tablet (no phone story)
 * a cinematic step has one framing: its target, lit by the spotlight, for the
 * whole of its narration. A ten-second line about the product then sits on
 * one button. A step whose document says what it is about (`Step.view`, set
 * by the director or the editor) is instead guided: it opens on the whole
 * screen when the screen is new, passes through the regions its shots name
 * (an `establish` or `result` shot drawn round part of the screen: a panel, a
 * form, a chart), and only then comes to its target — or, for an `overview`
 * step, rests on the last of those and never goes to a control.
 *
 * Opt-in: a step without `view` (every document published before this) frames
 * exactly as before. An `action` step rests where it always did
 * (`stepCamera`); the beats only come before it. Pure, shared by the player
 * (`stage.ts`) and the MP4 export (`export/plan.ts`). */

/** A region fills this share of the viewport on its tighter axis. */
export const GUIDE_REGION_FILL = 0.9;
/**
 * A region is never zoomed past this multiple of the whole-screen fit. A
 * panel a quarter of the screen wide would otherwise fill the window at 4×,
 * and the viewer loses which page it is on — the founder's complaint was
 * exactly a close-up held while the narration talked about the page.
 */
export const GUIDE_MAX_ZOOM = 2.2;
/** Click-through: how long a region is held before the camera moves on. */
export const GUIDE_REGION_HOLD_MS = 1300;
/**
 * Watch mode: the lead-in beats (the whole screen, the regions, the moves
 * between them) take this share of the step's narration. Narration in a demo
 * says where we are and what it is for first, and names the control last
 * ("…then press Continue"); without word timings this is the honest proxy.
 */
export const GUIDE_LEAD_SHARE = 0.7;
/** …and the control is on screen for at least this before the narration ends. */
export const GUIDE_TARGET_MIN_MS = 1800;
/**
 * An overview step's lead-in is shorter: it rests on the page (its region),
 * which is what its narration is about, so the whole screen only orients —
 * the same share a phone holds its establishing shot for (`WATCH_LEAD_SHARE`).
 * Measured on mado's own processing step: at 0.7 the card the line describes
 * came in only for its last two seconds.
 */
export const GUIDE_OVERVIEW_LEAD_SHARE = WATCH_LEAD_SHARE;

/** Does this step guide the camera through its narration on a desktop or tablet? */
export function guidedStep(
  step: Pick<Step, 'view' | 'scroll'> & { cues?: Step['cues'] },
  motion: Motion | undefined,
): boolean {
  return (
    motion === 'cinematic' &&
    !step.scroll &&
    (step.view === 'overview' || step.view === 'action' || !!step.cues?.length)
  );
}

export interface GuideInput {
  viewport: Viewport;
  asset: ShotAsset;
  allowFocus?: boolean;
  motion?: Motion | undefined;
}

/**
 * A region of the screen framed on a desktop viewport: filling
 * `GUIDE_REGION_FILL` of it, never past `GUIDE_MAX_ZOOM`, centred on the
 * region and pulled back so no empty space shows beside the asset. The whole
 * screen (or a region too big to be worth a zoom) is the screen fitted.
 */
export function regionLayout(
  input: GuideInput,
  rect: NormRect,
  /**
   * Where the window may cut the screen (`stepLines`), or just the lines a side may sit on
   * (`shotSides`). The window's edges are cleaned against them last (`cleanFrame`).
   */
  known: readonly Gutter[] | CleanLines = [],
  /** The closest it may come, times the whole-screen fit (a mark's frame: `CAMERA_MAX_ZOOM`). */
  maxZoom: number = GUIDE_MAX_ZOOM,
  /** False: the zoom is centred on `rect` before it is cleaned, its sides not snapped first. */
  snap = true,
): Layout {
  const lines: CleanLines = isGutterList(known) ? { x: known, y: [], rects: [] } : known;
  const zoomed = regionZoom(input, rect, snap ? lines.x : [], maxZoom);
  const whole = computeLayout({
    viewport: input.viewport,
    asset: input.asset,
    focus: FULL_FRAME,
    allowFocus: false,
  });
  if (zoomed.scale <= whole.scale + 1e-9) return zoomed;
  // The region keeps the air a mark's ring is drawn in (`CUE_AIR_PX`), as a lean judges it.
  return cleanFrame(zoomed, input.viewport, input.asset, lines, {
    floor: whole,
    keep: rect,
    padPx: CUE_AIR_PX,
  });
}

function isGutterList(k: readonly Gutter[] | CleanLines): k is readonly Gutter[] {
  return Array.isArray(k);
}

/** `regionLayout` before its edges are cleaned: the zoom, its sides snapped into `gutters`. */
function regionZoom(
  input: GuideInput,
  rect: NormRect,
  gutters: readonly Gutter[],
  maxZoom: number,
): Layout {
  const { viewport: vp, asset } = input;
  // The whole screen, fitted — never the legibility zoom's top-left crop
  // (`WHOLE_SCREEN_MIN_SHOWN`): measured on mado's own demo at 1440×900, that
  // crop (1.39×) cut off the New demo button the narration then went to. The
  // establishing shot is the page's shape; reading is the regions' job.
  const whole = computeLayout({ viewport: vp, asset, focus: FULL_FRAME, allowFocus: false });
  if (nearWhole(rect)) return whole;
  const contain = whole.containScale;
  const fill = Math.min(vp.w / (rect.w * asset.w), vp.h / (rect.h * asset.h)) * GUIDE_REGION_FILL;
  const scale = clamp(fill, contain, contain * maxZoom);
  if (scale < contain * CAMERA_MIN_ZOOM || scale <= whole.scale) return whole;
  const out = frame(vp, asset, rect, scale, contain, asset.medianTextH);
  // A window shaped to the viewport is usually wider than the region, and its sides then fall
  // wherever they fall, often mid-word ("Talentika komand…"). Widen it, never narrower, until
  // both sides sit on a clean cut: a shot's own side (snapped into a gutter at publish) or the
  // screen's edge.
  const sw = asset.w * scale;
  if (!gutters.length || sw <= vp.w + 0.5) return out;
  const w = vp.w / sw;
  // Text that reads at this zoom keeps reading: the window never widens past it (the film zooms
  // just far enough for its text, `legibleRect`).
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  const readable = textH * scale >= MIN_TEXT_PX ? MIN_TEXT_PX / textH : 0;
  const snap = snapSpan(
    -out.tx / sw,
    w,
    { a: rect.x, b: rect.x + rect.w },
    gutters,
    w,
    Math.min(1, vp.w / (asset.w * Math.max(contain, scale * SNAP_MIN_ZOOM, readable))),
  );
  if (!snap) return out;
  const s2 = vp.w / (snap.w * asset.w);
  if (s2 <= whole.scale) return whole;
  const at = frame(vp, asset, rect, s2, contain, asset.medianTextH);
  return { ...at, tx: clamp(-snap.l * asset.w * s2, vp.w - asset.w * s2, 0) };
}

/**
 * The sides of every shot a step carries, as places a desktop window may cut the screen. Publish
 * snaps each shot's sides into the screen's gutters (`@mado/autofocus/shots` `cleanSides`).
 */
export function shotSides(step: Pick<Step, 'shots' | 'scroll'>): Gutter[] {
  if (step.scroll || !step.shots?.length) return [];
  const out: Gutter[] = [];
  for (const s of step.shots) {
    for (const x of [s.rect.x, s.rect.x + s.rect.w]) {
      if (x > 0.001 && x < 0.999) out.push({ a: x, b: x });
    }
  }
  return out;
}

/**
 * A guided step's beats on a desktop viewport: the whole screen (when the
 * step arrives on a new screen, `establish`), then each lead-in shot's region
 * in order, then where the step rests — its target framing (`stepCamera`,
 * unchanged) for an `action` step; for an `overview` step the last region,
 * else its authored focus, else the whole screen. Consecutive beats that
 * frame the same pixels are one. A single beat means nothing moves.
 */
export function guideBeats(
  step: Step,
  input: GuideInput,
  opts: { establish?: boolean } = {},
): StoryBeat[] {
  const vp = input.viewport;
  const picture: PxRect = { x: 0, y: 0, w: vp.w, h: vp.h };
  const whole = regionLayout(input, FULL_FRAME);
  const sides = stepLines(step);
  const lead: StoryBeat[] = [];
  const push = (list: StoryBeat[], beat: StoryBeat): void => {
    const prev = list[list.length - 1];
    if (prev && nearLayout(prev.layout, beat.layout, picture)) {
      prev.reason = beat.reason;
      prev.holdMs = Math.max(prev.holdMs, beat.holdMs);
      return;
    }
    list.push(beat);
  };
  if (opts.establish) push(lead, { reason: 'establish', layout: whole, holdMs: ESTABLISH_HOLD_MS });
  for (const shot of orderedShots(step)) {
    if (shot.reason === 'target') continue;
    push(lead, {
      reason: shot.reason,
      layout: regionLayout(input, shot.rect, sides),
      holdMs: shot.holdMs ?? GUIDE_REGION_HOLD_MS,
    });
  }
  let end: Layout;
  if (step.view === 'overview') {
    const last = lead.pop();
    end = last
      ? last.layout
      : step.focus && !isFullFrame(step.focus)
        ? regionLayout(input, step.focus, sides)
        : whole;
  } else {
    end = stepCamera(step, {
      viewport: vp,
      asset: input.asset,
      allowFocus: input.allowFocus ?? true,
      motion: input.motion,
    });
  }
  const beats = [...lead];
  push(beats, { reason: 'target', layout: end, holdMs: 0 });
  beats[beats.length - 1]!.holdMs = 0;
  return beats;
}

/**
 * How long each beat is held, in order (the last: 0). Click-through, and
 * Watch mode without narration: the beats' own holds. Watch mode with
 * narration: the lead-in holds are stretched, in proportion, so the lead-in
 * (holds and moves) runs `GUIDE_LEAD_SHARE` of the narration, leaving the
 * control at least `GUIDE_TARGET_MIN_MS` of it — the whole screen and the
 * regions are on screen while the narration talks about them, the control
 * while it names it. Never shorter than click-through's holds.
 */
export function guideHolds(
  beats: readonly StoryBeat[],
  movesMs: readonly number[],
  narrationMs: number | null | undefined,
  /** The step's `view`: an overview leads in for `GUIDE_OVERVIEW_LEAD_SHARE`. */
  view?: StepView | undefined,
): number[] {
  const holds = beats.map((b, i) => (i === beats.length - 1 ? 0 : b.holdMs));
  if (!narrationMs || narrationMs <= 0 || beats.length < 2) return holds;
  const own = holds.reduce((a, b) => a + b, 0);
  const moves = movesMs.reduce((a, b) => a + b, 0);
  const share = view === 'overview' ? GUIDE_OVERVIEW_LEAD_SHARE : GUIDE_LEAD_SHARE;
  const lead = Math.min(narrationMs * share, narrationMs - GUIDE_TARGET_MIN_MS) - moves;
  if (lead <= own || own <= 0) return holds;
  return holds.map((h) => Math.round((h * lead) / own));
}

/**
 * The spotlight dims everything but the step's targets. On a phone
 * walkthrough, and on a guided `overview` step (about the page, not a
 * control), it is off unless the step turns it on; elsewhere it is on unless
 * the step turns it off (`Step.effects`). Shared by the player's overlay and
 * the exporter's `composeSpot`.
 */
export function spotlightWanted(
  step: Pick<Step, 'effects' | 'view' | 'scroll'>,
  opts: { walk?: boolean; motion?: Motion | undefined } = {},
): boolean {
  const quiet = opts.walk || (guidedStep(step, opts.motion) && step.view === 'overview');
  return quiet ? step.effects?.spotlight === true : step.effects?.spotlight !== false;
}

/** Vertical centre of `rect` framed by `layout`, as a share of the picture's height. */
export function centreInPicture(
  rect: NormRect,
  layout: Layout,
  asset: Pick<Asset, 'w' | 'h'>,
  picture: PxRect,
): number {
  const y = layout.ty + (rect.y + rect.h / 2) * asset.h * layout.scale;
  return (y - picture.y) / picture.h;
}

/* ── phone story: scroll steps ──────────────────────────────────────────────
 *
 * A stitched scroll of a landscape capture on a portrait phone. `scrollLayout`
 * covers the viewport's height and caps its zoom at `MAX_FOCUS_ZOOM`: on a
 * 2560px capture that is ~5px text in a slice cut off on both sides, and it
 * was the first thing a phone viewer of a desktop recording saw. In a story
 * the scroll is a walk down the page's content column instead, inside the
 * story's picture (the caption band carries the step's words):
 *
 *   - the column (the step's authored focus, else `ScrollSpec.column`, else
 *     the whole width) fills the picture's width when its text then reads, else the
 *     zoom is `SHOT_TEXT_PX` and a column wider than the picture is read from
 *     its left edge, the way a column of text is;
 *   - progress walks the content's rows — between the sticky bands, so the
 *     browser's bars and the taskbar never take a phone's screen — top to
 *     bottom; the sticky bands themselves are not drawn;
 *   - a sideways pan (`panPx`, the viewer's horizontal drag) reaches what the
 *     column leaves off, clamped to the asset.
 *
 * Pure, shared by the player (`stage.ts`) and the 9:16 social cut
 * (`export/frame.ts`), which frames the same walk on a reference phone. */

export interface ScrollStoryInput {
  /** The story's picture area (`storyFrame`), in viewport px. */
  picture: PxRect;
  asset: ShotAsset;
  scroll: Pick<ScrollSpec, 'from' | 'to' | 'sticky' | 'column'>;
  /** The step's authored focus: where to read across, before any column. */
  focus: NormRect | null;
}

/** The phone the export frames a scroll story on, in CSS px wide (`scrollStoryLayoutOn`). */
export const STORY_REF_W = 390;
/** Content rows shorter than this share of the tall asset are not trusted: the whole height is walked. */
const STORY_MIN_ROWS = 0.2;

/**
 * How wide a page's content column is assumed to be, in lines of its own body
 * text, when none was found: generous on purpose (a 960 px column of 9 px
 * text is ~107), so the guessed column's left edge falls in the margin just
 * before the real one rather than inside it.
 */
export const ASSUMED_COLUMN_TEXT_H = 120;

/**
 * Where a scroll story reads across the page: an authored focus (the author
 * said where to look — auto focus never sets one on a scroll step), else the
 * column found in the pixels — both read from their left edge when wider than
 * the picture — else, with no pixels at runtime, a guessed column: a centred
 * band `ASSUMED_COLUMN_TEXT_H` text lines wide (the whole page when that is
 * wider), read from its left edge. Lines of text start at a column's left, so
 * that is where a phone-sized window finds words, whether the page centres
 * its content or hugs the left. Centring on the page's middle instead showed
 * the empty right half of every left-aligned row: a full-page capture of a
 * 1280 px report opened on a blank window under its headline. `asset`
 * absent: the whole width, centred.
 */
export function scrollColumn(
  scroll: Pick<ScrollSpec, 'column'>,
  focus: NormRect | null,
  asset?: ShotAsset,
): { x: number; w: number; start: boolean } {
  if (focus && !isFullFrame(focus)) return { x: focus.x, w: focus.w, start: true };
  if (scroll.column) return { x: scroll.column.x, w: scroll.column.w, start: true };
  if (asset && asset.w > 0) {
    const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
    const w = Math.min(1, (ASSUMED_COLUMN_TEXT_H * textH) / asset.w);
    if (w > 0) return { x: (1 - w) / 2, w, start: true };
  }
  return { x: 0, w: 1, start: false };
}

/** The content rows a scroll story walks: between the sticky bands. */
export function scrollRows(sticky: { top: number; bottom: number }): { y0: number; y1: number } {
  const y0 = sticky.top;
  const y1 = 1 - sticky.bottom;
  return y1 - y0 >= STORY_MIN_ROWS ? { y0, y1 } : { y0: 0, y1: 1 };
}

/** The zoom a scroll story reads its column at. */
export function scrollStoryScale(picture: PxRect, asset: ShotAsset, column: { w: number }): number {
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  const fitCol = picture.w / (column.w * asset.w);
  const scale =
    fitCol * textH >= MIN_TEXT_PX
      ? Math.min(fitCol, SHOT_MAX_TEXT_PX / textH)
      : SHOT_TEXT_PX / textH;
  return Math.max(picture.w / asset.w, scale);
}

/** Scrollable distance of a scroll story, in px: the content's height at the zoom, less the picture's. */
export function scrollStoryRange(input: ScrollStoryInput): number {
  const { picture, asset, scroll } = input;
  const scale = scrollStoryScale(picture, asset, scrollColumn(scroll, input.focus, asset));
  const { y0, y1 } = scrollRows(scroll.sticky);
  return Math.max(0, (y1 - y0) * asset.h * scale - picture.h);
}

/**
 * A scroll story's framing at `progress` (0..1 through `from..to`), panned
 * sideways by `panPx` from the column's own placement (clamped: no empty
 * space ever shows beside the asset).
 */
export function scrollStoryLayout(input: ScrollStoryInput, progress: number, panPx = 0): Layout {
  const { picture, asset, scroll } = input;
  const col = scrollColumn(scroll, input.focus, asset);
  const scale = scrollStoryScale(picture, asset, col);
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  const sw = asset.w * scale;
  const sh = asset.h * scale;
  let tx: number;
  if (sw <= picture.w) {
    tx = (picture.w - sw) / 2;
  } else {
    tx =
      col.w * sw > picture.w && col.start
        ? LEAD_PAD_PX - col.x * sw
        : picture.w / 2 - (col.x + col.w / 2) * sw;
    tx = clamp(tx + panPx, picture.w - sw, 0);
  }
  const { y0, y1 } = scrollRows(scroll.sticky);
  const top = y0 * sh;
  const contentH = (y1 - y0) * sh;
  const at = scroll.from + (scroll.to - scroll.from) * clamp(progress, 0, 1);
  const ty =
    contentH <= picture.h ? (picture.h - contentH) / 2 - top : -top - at * (contentH - picture.h);
  return {
    mode: 'focus',
    scale,
    tx: picture.x + tx,
    ty: picture.y + ty,
    containScale: picture.w / asset.w,
    textPx: textH * scale,
  };
}

/**
 * A scroll story's walk in: on arrival from anything but another scroll
 * (`establish`), the whole page first — where we are — then the column's
 * top, where the scroll begins. Straight on from a scroll, just the column.
 */
export function scrollStoryBeats(input: ScrollStoryInput, establish: boolean): StoryBeat[] {
  const rest: StoryBeat = { reason: 'target', layout: scrollStoryLayout(input, 0), holdMs: 0 };
  if (!establish) return [rest];
  // The page's content, like a still's establish shot: the column, all of
  // it, without the browser's bars — not the whole desktop round it.
  const { y0, y1 } = scrollRows(input.scroll.sticky);
  const col = scrollColumn(input.scroll, input.focus, input.asset);
  const overview = shotLayout(input.picture, input.asset, {
    x: col.x,
    y: y0,
    w: col.w,
    h: y1 - y0,
  });
  if (nearLayout(overview, rest.layout, input.picture)) return [rest];
  return [{ reason: 'establish', layout: overview, holdMs: SHOT_HOLD_MS.establish }, rest];
}

/**
 * `scrollStoryLayout` for a canvas measured in output pixels (the 9:16 social
 * cut): laid out on a `STORY_REF_W`-wide phone of the canvas's shape, then
 * scaled up, so the video shows the part of the page the phone does.
 */
export function scrollStoryLayoutOn(
  target: Viewport,
  input: Omit<ScrollStoryInput, 'picture'>,
  progress: number,
  /** The target's width in phone px (the story card's window is narrower than the phone). */
  refW = STORY_REF_W,
): Layout {
  const k = target.w / refW;
  const picture = { x: 0, y: 0, w: refW, h: target.h / k };
  const l = scrollStoryLayout({ ...input, picture }, progress);
  return {
    ...l,
    scale: l.scale * k,
    tx: l.tx * k,
    ty: l.ty * k,
    containScale: target.w / input.asset.w,
    textPx: l.textPx * k,
  };
}

/* ── narration cues ─────────────────────────────────────────────────────────
 *
 * docs/plans/desktop-guided-camera.md §cues. A step's `cues` tie phrases of
 * its narration to parts of its screen: while the narration says "click
 * through", the camera is on a demo card. When each phrase is spoken comes
 * from `Cue.atMs` when it was measured, else from the text: a word's share
 * of the line in syllables, with the pauses punctuation makes. Fitted to
 * mado's own ElevenLabs clips (27 phrase starts after a pause, measured with
 * a silence detector): 0.19 s RMS, 0.39 s worst. Pure, shared by the player
 * (desktop, tablet, phone) and the MP4 export. */

/** Units of speech: per syllable 1, between words… */
export const SPEECH_WORD_GAP = 0.4;
/** …after a comma, colon or semicolon… */
export const SPEECH_COMMA_PAUSE = 1.5;
/** …after a full stop, question or exclamation mark. */
export const SPEECH_STOP_PAUSE = 4;
/** The silence a generated clip ends on, not spoken. */
export const SPEECH_TAIL_MS = 200;
/** Click-through, and Watch without sound: the pace a line is "read" at (a voice's pace, fitted the same way). */
export const READING_UNIT_MS = 170;
/**
 * Watch without a voice: someone reading an unfamiliar card while the screen moves reads about
 * 140 words a minute, not a voice's ~190. The walk's clock (rings, labels, camera, the overview's
 * arrival), the step's dwell and the CTA wait are all stretched by this (founder, 2026-10-16:
 * "still goes a bit too fast, especially where a bit longer text is"; 1.4 was then "a bit too
 * slow", so a few percent off).
 */
export const SILENT_READ_FACTOR = 1.32;
/** A cue's camera move starts this long before its phrase, so it arrives on the word. */
export const CUE_LEAD_MS = 250;
/** Cues closer than this are spaced out: a move needs time to be seen. */
export const CUE_MIN_GAP_MS = 700;
/** A cue is held at least this long before the camera relaxes or the control comes. */
export const CUE_HOLD_MS = 1400;
/** A cue's mark fades in and out over this (player `.mado-cue`, export `composeCue`). */
export const CUE_FADE_MS = 320;
/** Two zoomed cues further apart than this (centres, share of the screen) relax through the whole screen. */
export const CUE_FAR = 0.3;
/**
 * Room a cue's mark needs between its rect and the picture's edge, in screen
 * px: the ring's padding (8) and line (2.5), and some air. A rect closer to the
 * edge than this would have its ring cut by the window.
 */
export const CUE_AIR_PX = 16;
/**
 * A phone: a `highlight` or `look` narrower than this share of the picture at the
 * current framing is framed like a zoom: seen whole-screen it is a ring round text
 * too small to read.
 */
export const CUE_SMALL_SHARE = 0.7;
/**
 * A desktop: a `highlight` narrower and shorter than this share of the window, seen on the whole
 * screen, is leaned into (`CuePlanInput.lean`): a ring round a menu row or a side panel at
 * whole-page scale is text a laptop viewer squints at (desktop-polish F7). Wider, the mark is about
 * the page and the page stays whole (founder, 2026-10-08: "frames not zooms on broad areas").
 */
export const DESKTOP_CUE_SMALL_SHARE = 0.5;

/** Is `r` small under layout `l` on a desktop window (`DESKTOP_CUE_SMALL_SHARE` on both axes)? */
export function desktopCueSmall(
  r: NormRect,
  l: Layout,
  picture: Pick<PxRect, 'w' | 'h'>,
  asset: Pick<Asset, 'w' | 'h'>,
): boolean {
  return (
    r.w * asset.w * l.scale < DESKTOP_CUE_SMALL_SHARE * picture.w &&
    r.h * asset.h * l.scale < DESKTOP_CUE_SMALL_SHARE * picture.h
  );
}

/** A line at least this long, on a step with no cues, drifts (`CuePlanInput.drift`). */
export const DRIFT_MIN_MS = 5500;
/** The drift starts this share of the way through the line and arrives as it ends. */
export const DRIFT_FROM = 0.3;
/** The area a drift pushes toward, as a share of the screen's width and height. */
export const DRIFT_SPAN = 0.6;
/** A phone story's cue zooms no closer than this times the whole screen. */
export const CUE_MAX_ZOOM = 3.5;

function syllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!w) return 1;
  const m = w.replace(/e$/, '').match(/[aeiouy]+/g);
  return Math.max(1, m ? m.length : 1);
}
const norm = (w: string): string => w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** The narration's words and where each starts, in speech units; the line's total. */
export function speechUnits(text: string): { words: string[]; at: number[]; total: number } {
  const words = text.split(/\s+/).filter(Boolean);
  const at: number[] = [];
  let u = 0;
  words.forEach((w, i) => {
    at.push(u);
    u += syllables(w) + SPEECH_WORD_GAP;
    if (i === words.length - 1) return;
    if (/[.!?]["')\]]?$/.test(w)) u += SPEECH_STOP_PAUSE;
    else if (/[,;:]["')\]]?$/.test(w)) u += SPEECH_COMMA_PAUSE;
  });
  return { words, at, total: u };
}

/** The word index `phrase` starts at in `text` (case and punctuation ignored), or -1. */
export function phraseIndex(text: string, phrase: string): number {
  const words = text.split(/\s+/).filter(Boolean).map(norm);
  const want = phrase.split(/\s+/).map(norm).filter(Boolean);
  if (!want.length) return -1;
  for (let i = 0; i + want.length <= words.length; i++) {
    if (want.every((w, k) => words[i + k] === w)) return i;
  }
  return -1;
}

/** How long the line takes at reading pace (click-through), in ms. */
export function readingMs(text: string): number {
  return Math.round(speechUnits(text).total * READING_UNIT_MS) + SPEECH_TAIL_MS;
}

/**
 * When `phrase` is spoken, in ms from the start of the clip (`durationMs`
 * long; null: at reading pace). Null when the phrase is not in the text.
 */
export function phraseAtMs(
  text: string,
  phrase: string,
  durationMs: number | null | undefined,
): number | null {
  const i = phraseIndex(text, phrase);
  if (i < 0) return null;
  const { at, total } = speechUnits(text);
  const dur = durationMs && durationMs > 0 ? durationMs : readingMs(text);
  return total > 0 ? Math.round((at[i]! / total) * Math.max(0, dur - SPEECH_TAIL_MS)) : 0;
}

/** A cue placed on the step's clock: its effect shows from `startMs` to `endMs`. */
export interface TimedCue {
  cue: Cue;
  startMs: number;
  endMs: number;
  /**
   * The cue as it is drawn (`cueMark`): a `look` off the tap target is a highlight, and on a
   * phone a ring too big for the window is the part of it inside the window. Set by the plans;
   * absent means `cue` as it is.
   */
  mark?: Cue;
}

/** A camera beat at an absolute time from the start of the narration. */
export interface CueBeat {
  atMs: number;
  layout: Layout;
  /** `lean`: the camera's own move closer to a small highlight (a `zoom` cue is `cue`). */
  reason: 'establish' | 'cue' | 'lean' | 'relax' | 'target' | 'drift';
  /** This move's own length when it is not the viewport's pace (a slow drift). */
  ms?: number;
}

export interface CuePlan {
  beats: CueBeat[];
  cues: TimedCue[];
  /** When the camera starts for the control (the target beat), or null for an overview. */
  targetMs: number | null;
}

export interface CuePlanInput {
  /** The whole screen, as this viewport frames it. */
  whole: Layout;
  /** A rect framed for a `zoom` cue. */
  frame: (rect: NormRect) => Layout;
  /** Where an action step rests (its control); null for an overview. */
  target: Layout | null;
  /** The clip's length, or null: reading pace. */
  narrationMs: number | null | undefined;
  /** How long a camera move between two framings takes on this viewport. */
  moveMs: (a: Layout, b: Layout) => number;
  /** The area the layouts are framed in (a phone story's picture, else the viewport). */
  picture: PxRect;
  asset: Pick<Asset, 'w' | 'h'>;
  /**
   * A step with no cues drifts while its line is told (a long line on one
   * still screen): a slow push toward the area the step is about, so the
   * picture is never still for ten seconds. Off by default.
   */
  drift?: boolean;
  /**
   * A `highlight` or `look` whose rect is not on screen with `CUE_AIR_PX` to
   * spare, or is small at the current framing (`CUE_SMALL_SHARE`): framed like
   * a `zoom` (a phone, where the whole screen is small and a mark at its edge
   * sits on the window's rounded frame) instead of going back to the whole
   * screen (desktop, the default).
   */
  frameOffScreen?: boolean;
  /**
   * Two zoomed cues far apart go back through the whole screen between them (`CUE_FAR`).
   * False on a phone: there the whole screen is text a few pixels high, so the step out and
   * back in is a blur that comes and goes (founder, 2026-10-12); the camera pans instead.
   */
  relax?: boolean;
  /** A phone: a ring the window cannot hold whole is drawn clipped to it (`markCues`). */
  clipMarks?: boolean;
  /**
   * A desktop: a `highlight` that is small on the whole screen (`small`, judged at `whole`) is
   * framed the way a `zoom` is, when the camera is on the whole screen or the mark is off the
   * current frame: the first of `frames` that holds it with `CUE_AIR_PX` round it (none at a side
   * where the screen itself ends). The marks after it that fit in one frame with it share it: a
   * menu read row by row is framed once, round the rows, and holds (desktop-polish F7).
   */
  lean?: {
    small: (r: NormRect, l: Layout) => boolean;
    frames: (r: NormRect) => Layout[];
  };
  /**
   * A phone: the whole screen is a glance, not a place to rest. When the first framed cue (else
   * the control) is further off than this, the camera moves there at this time, so the line is
   * not told over an unreadable screen (founder, 2026-10-12: "on a phone it is unusable").
   * Its mark still waits for its phrase.
   */
  settleMs?: number;
  /**
   * A desktop: after every camera move the picture rests at least this long, and a new screen is
   * seen whole this long before the first move. A lean or a relax that cannot keep to it is not
   * made (its mark is lit where the camera is); a move the line needs (a `zoom`, a mark off the
   * frame, the control) waits for it. The camera went in, out and in again with holds under a
   * second, and leaned in before a new screen had been seen (founder, 2026-10-14: "rushes too
   * fast"). Absent: no rest (a phone, which has its own pace).
   */
  restMs?: number;
  /**
   * A desktop: where the camera already is as the step starts, when the step before left it on
   * this same screen (`desktopEntry`). The screen has been seen, so it is not shown whole again:
   * the camera went out to the whole screen at every step on one page and leaned back in 1.5 s
   * later (player-feel 1, 2026-10-15). Absent: the whole screen, held `restMs`.
   */
  entry?: Layout | null;
  /**
   * A desktop: a camera that keeps one framing and moves only when it must (player-feel 1,
   * 2026-10-15: whole, in, out, in, out every few seconds). With it a mark off the frame while
   * the camera is in close is reached by `shift` (the same zoom, the frame shifted, first; else
   * another close frame) before any zoom out; a zoom whose own frame is wider than the camera's
   * (a whole row, Talentika s6) pans to it when it fits at this zoom; two zooms far apart pan
   * between them instead of going back through the whole screen; a zoom already as close as its cue wants, with the mark in frame, holds; and the
   * control in frame and readable is not moved to. Absent: a phone, which has its own pace.
   */
  calm?: {
    /** The frame at the zoom it has, shifted to hold `rect`; null when it cannot. */
    pan: (rect: NormRect, from: Layout) => Layout | null;
    /** Where to go for a mark off the frame: the pan first, then close frames round it. */
    shift: (rect: NormRect, from: Layout) => Layout[];
  };
}

/** Desktop: how long the picture rests after a camera move, and a new screen is seen whole. */
export const DESKTOP_CAMERA_REST_MS = 1500;

/**
 * A calm camera (`CuePlanInput.calm`) in at least this share of a zoom cue's own scale, the mark
 * in frame and readable, does not move for it: a 15% push is a twitch, not a closer look.
 */
export const CALM_ZOOM_NEAR = 0.85;

/** Is `r` wholly inside `picture` under layout `l`? */
export function rectOnScreen(
  r: NormRect,
  l: Layout,
  picture: PxRect,
  asset: Pick<Asset, 'w' | 'h'>,
): boolean {
  const x0 = l.tx + r.x * asset.w * l.scale;
  const y0 = l.ty + r.y * asset.h * l.scale;
  const x1 = x0 + r.w * asset.w * l.scale;
  const y1 = y0 + r.h * asset.h * l.scale;
  return (
    x0 >= picture.x - 1 &&
    y0 >= picture.y - 1 &&
    x1 <= picture.x + picture.w + 1 &&
    y1 <= picture.y + picture.h + 1
  );
}

/**
 * The step's camera and cues on the narration's clock. Opens on the whole
 * screen; each cue starts at its phrase (its move `CUE_LEAD_MS` before):
 * `zoom` frames its rect; `highlight` and `look` keep the camera where it is
 * when the rect is on screen there, else go back to the whole screen (on a
 * phone: frame it, `frameOffScreen`). Two
 * zoomed cues far apart relax through the whole screen when there is time.
 * An action step then comes to its control (`target`) at the later of the
 * last cue's hold and `GUIDE_TARGET_MIN_MS` before the line ends; an
 * overview stays on its last framing. Each cue's effect lasts until the next
 * cue starts, or the control.
 */
export function cuePlan(
  step: Pick<Step, 'cues' | 'narration' | 'hotspots'> & { next?: Step['next'] },
  input: CuePlanInput,
): CuePlan {
  const cues = step.cues ?? [];
  const text = step.narration?.text ?? cues.map((c) => c.phrase).join(' ');
  const lineMs = input.narrationMs && input.narrationMs > 0 ? input.narrationMs : readingMs(text);
  const calm = input.calm;
  /** In closer than the whole screen. */
  const close = (l: Layout): boolean => l.scale > input.whole.scale * 1.02;
  // The same screen as the step before: the camera carries on from where that step left it.
  const start = input.entry ?? input.whole;
  const beats: CueBeat[] = [{ atMs: 0, layout: start, reason: 'establish' }];
  const timed: TimedCue[] = [];
  let cur = start;
  const rest = input.restMs ?? 0;
  /** When the camera has arrived (a mark waits for it). */
  let free = 0;
  /** When the next move may start: arrived, plus the rest (`restMs`). A screen already seen
   *  (`entry`) has had its rest: the step before held it while its line ended. */
  let next = input.entry ? 0 : rest;
  if (input.entry) {
    // The first framing this step comes in to for its rings when it opens whole (or the whole
    // screen, when it stays there). Near where the camera is, the camera stays; elsewhere it goes
    // there at once, in one move: the step before framed its own subject, and that frame cut the
    // rows of a list this step is about (Talentika s8, a filtered list after the status menu).
    const own = cuePlan(step, { ...input, entry: null });
    const want =
      own.beats.find((b) => b.reason === 'lean' || b.reason === 'cue')?.layout ?? input.whole;
    if (!nearLayout(input.entry, want, input.picture)) {
      beats.push({ atMs: 0, layout: want, reason: close(want) ? 'lean' : 'relax' });
      free = input.moveMs(cur, want);
      next = free + rest;
      cur = want;
    }
  }

  let prevAt = -Infinity;
  let prevZoom: NormRect | null = null;
  let lean: { rect: NormRect; to: Layout } | null = null;
  const push = (atMs: number, layout: Layout, reason: CueBeat['reason']): boolean => {
    if (sameLayout(cur, layout)) return false;
    const at = Math.max(atMs, free, next);
    beats.push({ atMs: at, layout, reason });
    free = at + input.moveMs(cur, layout);
    next = free + rest;
    cur = layout;
    return true;
  };
  /** A move that is only nice to have (a lean, a relax) is made only when it keeps the rest. */
  const canRest = (startMs: number): boolean => !rest || startMs >= next;
  if (input.settleMs != null) {
    const first = cues[0];
    const to = first ? input.frame(first.rect) : input.target;
    const arrives = first
      ? (first.atMs ?? phraseAtMs(text, first.phrase, lineMs))
      : input.target
        ? lineMs - GUIDE_TARGET_MIN_MS
        : null;
    if (to && arrives != null && arrives - CUE_LEAD_MS > input.settleMs + CUE_HOLD_MS) {
      push(input.settleMs, to, first ? 'cue' : 'target');
    }
  }
  const air = insetRect(input.picture, CUE_AIR_PX);
  // The frame a lean into cue `i` takes: round it and the marks after it that fit in one frame
  // with it (a `zoom` ends the run: it frames itself). Null when no frame holds cue `i` closer
  // than the whole screen.
  // A lean keeps air round a mark, except at a side where the screen itself ends: there the
  // whole screen cuts it just the same.
  const room = (l: Layout): PxRect => {
    const p = input.picture;
    const x0 = l.tx >= p.x - 1 ? p.x : air.x;
    const y0 = l.ty >= p.y - 1 ? p.y : air.y;
    const x1 = l.tx + input.asset.w * l.scale <= p.x + p.w + 1 ? p.x + p.w : air.x + air.w;
    const y1 = l.ty + input.asset.h * l.scale <= p.y + p.h + 1 ? p.y + p.h : air.y + air.h;
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  };
  const leanFrom = (i: number, lean: Pick<NonNullable<CuePlanInput['lean']>, 'frames'>) => {
    const frameFor = (rs: NormRect[]): Layout | null => {
      for (const l of lean.frames(unionRects(rs)!)) {
        // Closer than the whole screen by less than `CAMERA_MIN_ZOOM` is a crop, not a lean.
        if (l.scale < input.whole.scale * CAMERA_MIN_ZOOM) continue;
        const box = room(l);
        if (rs.every((r) => rectOnScreen(r, l, box, input.asset))) return l;
      }
      return null;
    };
    let group = [cues[i]!.rect];
    let to = frameFor(group);
    if (!to) return null;
    for (let j = i + 1; j < cues.length && cues[j]!.effect !== 'zoom'; j++) {
      const more = [...group, cues[j]!.rect];
      const t2 = frameFor(more);
      if (!t2) break;
      group = more;
      to = t2;
    }
    return { rect: unionRects(group)!, to };
  };
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i]!;
    const moves = beats.length;
    const spoken = cue.atMs ?? phraseAtMs(text, cue.phrase, lineMs);
    const at = Math.max(spoken ?? prevAt + CUE_HOLD_MS, prevAt + CUE_MIN_GAP_MS, 0);
    // The move starts early enough to arrive on the word: a move takes its own
    // time (walkMoveDuration), and a fixed short lead left the camera landing,
    // and the mark fading in, well after the phrase (quality gate, 2026-10-12).
    const moveAt = (to: Layout): number => Math.max(0, at - input.moveMs(cur, to));
    // On screen before the move (a zoom into what is already there): the mark rides the move.
    const onScreen = rectOnScreen(cue.rect, cur, insetRect(input.picture, CUE_AIR_PX), input.asset);
    // Seen in the current frame the way a lean judges it (`room`).
    const inRoom = input.lean ? rectOnScreen(cue.rect, cur, room(cur), input.asset) : onScreen;
    let leaned = false;
    // Calm: the camera in close, the mark off its frame. The frame keeps its size and shifts to
    // the mark (and the marks after it that fit with it), instead of going out and back in.
    const pan =
      calm && cue.effect !== 'zoom' && close(cur) && !inRoom
        ? leanFrom(i, { frames: (r) => calm.shift(r, cur) })
        : null;
    if (cue.effect === 'zoom') {
      const to = input.frame(cue.rect);
      // Calm: already in about as close as the zoom wants, its mark in frame and readable: the
      // frame holds.
      const holds =
        !!calm &&
        close(cur) &&
        inRoom &&
        cur.textPx >= MIN_TEXT_PX &&
        cur.scale >= to.scale * CALM_ZOOM_NEAR;
      if (holds) {
        // Nothing moves; the mark is lit where the camera is.
      } else if (!calm && input.relax !== false && prevZoom && !sameLayout(cur, to)) {
        const dx = prevZoom.x + prevZoom.w / 2 - (cue.rect.x + cue.rect.w / 2);
        const dy = prevZoom.y + prevZoom.h / 2 - (cue.rect.y + cue.rect.h / 2);
        const relaxAt = at - input.moveMs(input.whole, to) - input.moveMs(cur, input.whole) - 150;
        if (
          Math.hypot(dx, dy) > CUE_FAR &&
          relaxAt >= Math.max(free, prevAt + CUE_HOLD_MS * 0.6) &&
          canRest(relaxAt)
        ) {
          push(relaxAt, input.whole, 'relax');
        }
      }
      // Calm: a zoom whose frame is wider than where the camera is (a whole row, after a lean
      // into a word in it) went out to the whole screen and leaned back in a second later (13 Oct
      // hand-made demo s6). When the mark fits at this zoom, the frame shifts to it instead.
      const across =
        !holds && calm && close(cur) && to.scale < cur.scale * 0.98
          ? calm.pan(cue.rect, cur)
          : null;
      const shifted =
        across &&
        across.textPx >= MIN_TEXT_PX &&
        rectOnScreen(cue.rect, across, room(across), input.asset)
          ? across
          : null;
      if (!holds) push(moveAt(shifted ?? to), shifted ?? to, 'cue');
      prevZoom = cue.rect;
    } else if (pan) {
      push(moveAt(pan.to), pan.to, 'lean');
      prevZoom = pan.rect;
      leaned = true;
    } else if (
      (lean =
        input.lean &&
        cue.effect === 'highlight' &&
        (cur.scale <= input.whole.scale + 1e-4 || !inRoom) &&
        input.lean.small(cue.rect, input.whole)
          ? leanFrom(i, input.lean)
          : null) &&
      // On screen and readable already, a lean that would break the rest is not made: the mark
      // is lit where the camera is. Unreadable there, the lean waits for the rest instead.
      (!inRoom || cur.textPx < MIN_TEXT_PX || canRest(moveAt(lean.to)))
    ) {
      push(moveAt(lean.to), lean.to, 'lean');
      prevZoom = lean.rect;
      leaned = true;
    } else if (
      !inRoom ||
      (input.frameOffScreen &&
        (cue.rect.w * input.asset.w * cur.scale < CUE_SMALL_SHARE * input.picture.w ||
          cur.textPx < MIN_TEXT_PX))
    ) {
      if (input.frameOffScreen) {
        const to = input.frame(cue.rect);
        push(moveAt(to), to, 'cue');
        prevZoom = cue.rect;
      } else {
        push(moveAt(input.whole), input.whole, 'relax');
        prevZoom = null;
      }
    }
    // A move the camera could not start on time (the last one still running)
    // arrives late: the mark waits for it, or it is drawn where the camera is
    // not yet looking.
    // A lean held back by the rest (`restMs`): the mark is lit on its words where the camera is,
    // and rides the lean in when it comes, as an editor rings a thing and then pushes in.
    const seen = onScreen || (leaned && inRoom);
    const shown = beats.length > moves && !seen ? Math.max(at, free - CUE_LEAD_MS) : at;
    // The last mark goes as the camera leaves it, not as this one comes: it
    // would ride the pan off the window's edge.
    const leaves = beats.length > moves ? beats[moves]!.atMs : shown;
    const prev = timed[timed.length - 1];
    if (prev) prev.endMs = Math.max(prev.startMs, Math.min(shown, leaves));
    timed.push({ cue, startMs: shown, endMs: lineMs });
    prevAt = shown;
  }
  if (input.drift && !cues.length && lineMs >= DRIFT_MIN_MS) {
    const at = lineMs * DRIFT_FROM;
    const ms = Math.max(0, lineMs - at - 400);
    // Toward the control the step leads to, else the middle of the screen.
    const g = step.hotspots?.[0]?.geom;
    const cx = g ? g.x + g.w / 2 : 0.5;
    const cy = g ? g.y + g.h / 2 : 0.5;
    const x = Math.max(0, Math.min(1 - DRIFT_SPAN, cx - DRIFT_SPAN / 2));
    const y = Math.max(0, Math.min(1 - DRIFT_SPAN, cy - DRIFT_SPAN / 2));
    const to = input.frame({ x, y, w: DRIFT_SPAN, h: DRIFT_SPAN });
    if (!sameLayout(cur, to)) {
      beats.push({ atMs: at, layout: to, reason: 'drift', ms });
      free = at + ms;
      cur = to;
    }
  }
  let targetMs: number | null = null;
  if (input.target) {
    const want = Math.max(timed.length ? prevAt + CUE_HOLD_MS : 0, lineMs - GUIDE_TARGET_MIN_MS);
    // Calm: the camera in close with the control in frame and readable stays: a last move to it
    // would only reframe what is already shown.
    const box = room(cur);
    const shown =
      !!calm &&
      close(cur) &&
      cur.textPx >= MIN_TEXT_PX &&
      !!step.hotspots?.length &&
      step.hotspots.every((h) => rectOnScreen(h.geom, cur, box, input.asset));
    targetMs =
      !shown && push(want, input.target, 'target')
        ? beats[beats.length - 1]!.atMs
        : Math.max(want, free);
    const last = timed[timed.length - 1];
    if (last) last.endMs = Math.max(last.startMs, targetMs - CUE_FADE_MS);
  }
  return markCues(step, { beats, cues: timed, targetMs }, input.clipMarks ? input : null);
}

/** `cuePlan` on a desktop or tablet viewport (no phone story): the guide's framings and pace. */
export function cuePlanDesktop(
  step: Step,
  input: GuideInput,
  narrationMs: number | null | undefined,
  /** Where the step before left the camera on this same screen (`desktopEntry`). */
  entry?: Layout | null,
): CuePlan {
  const vp = input.viewport;
  // A cue's own sides are clean (a section reaches its frame, `regions.ts`), and so are the
  // step's shots' sides.
  const lines = stepLines(step);
  const sides = (r: NormRect): CleanLines => ({
    ...lines,
    x: [
      ...lines.x,
      ...[r.x, r.x + r.w].filter((x) => x > 0.001 && x < 0.999).map((x) => ({ a: x, b: x })),
    ],
  });
  const picture = { x: 0, y: 0, w: vp.w, h: vp.h };
  // The frames for `r`, the one snapped to clean cuts first. A snapped frame that leaves `r` well
  // off the window's middle goes last: on a centred column (a processing card, a form) the
  // screen's own edge is a clean cut too, and the window then sat on it with the card pushed
  // against its far side (founder, 2026-10-14). Judged on the smallest shot that holds `r` (the
  // card it sits in), when there is one.
  const framesFor = (r: NormRect, known: CleanLines, maxZoom: number): Layout[] => {
    const snapped = regionLayout(input, r, known, maxZoom);
    const holder = (step.shots ?? [])
      .map((s) => s.rect)
      .filter((s) => rectContains(s, r) && !nearWhole(s))
      .sort((a, b) => a.w * a.h - b.w * b.h)[0];
    const subject = holder ?? r;
    if (offCentre(snapped, subject, vp, input.asset) <= CUE_OFF_CENTRE) {
      return [snapped, regionLayout(input, r, known, maxZoom, false)];
    }
    const tries = [
      ...(holder ? [regionLayout(input, holder, known, maxZoom, false)] : []),
      regionLayout(input, r, known, maxZoom, false),
    ];
    // Cleaning can still slide a centred window sideways: its top just grazes a row at the
    // page's edge (an app bar), and the side moves off that row's words. Keeping the room
    // either side of the subject in view makes the clean cut a top or bottom instead.
    const cx = subject.x + subject.w / 2;
    for (const t of tries.slice()) {
      if (offCentre(t, subject, vp, input.asset) <= CUE_OFF_CENTRE) continue;
      const half = (vp.w / (input.asset.w * t.scale) / 2) * 0.98;
      const x0 = Math.max(0, cx - half);
      const wide = { x: x0, y: subject.y, w: Math.min(1, cx + half) - x0, h: subject.h };
      tries.push(regionLayout(input, wide, known, maxZoom, false));
    }
    const centred = tries.sort(
      (a, b) => offCentre(a, subject, vp, input.asset) - offCentre(b, subject, vp, input.asset),
    );
    return [...centred, snapped];
  };
  const air = insetRect(picture, CUE_AIR_PX);
  const whole = regionLayout(input, FULL_FRAME);
  // A pan: the frame at the zoom it has, shifted along an axis only where `r` does not fit across
  // it (there centred on `r`), then cleaned like any frame (never closer).
  const pan = (r: NormRect, from: Layout): Layout | null => {
    const sw = input.asset.w * from.scale;
    const sh = input.asset.h * from.scale;
    const axis = (t: number, start: number, len: number, size: number, port: number): number => {
      const a = t + start * size;
      const b = a + len * size;
      if (a >= CUE_AIR_PX - 1 && b <= port - CUE_AIR_PX + 1) return t;
      return size <= port
        ? (port - size) / 2
        : clamp(port / 2 - (start + len / 2) * size, port - size, 0);
    };
    if (r.w * sw > vp.w - 2 * CUE_AIR_PX || r.h * sh > vp.h - 2 * CUE_AIR_PX) return null;
    const moved: Layout = {
      ...from,
      tx: axis(from.tx, r.x, r.w, sw, vp.w),
      ty: axis(from.ty, r.y, r.h, sh, vp.h),
    };
    const cleaned = cleanFrame(moved, vp, input.asset, lines, {
      floor: whole,
      keep: r,
      padPx: CUE_AIR_PX,
    });
    return cleaned.scale >= from.scale * CALM_ZOOM_NEAR ? cleaned : null;
  };
  // Where a calm camera goes for a mark off its frame: the pan, else a lean's frames for it
  // (still in close: no way out and back in).
  const shift = (r: NormRect, from: Layout): Layout[] => {
    const p = pan(r, from);
    return [...(p ? [p] : []), ...framesFor(r, lines, CAMERA_MAX_ZOOM)];
  };
  return cuePlan(step, {
    whole,
    // A `zoom` cue: the first frame that holds its rect with air (as a lean judges it).
    frame: (r) => {
      const all = framesFor(r, sides(r), GUIDE_MAX_ZOOM);
      return all.find((l) => rectOnScreen(r, l, air, input.asset)) ?? all[all.length - 1]!;
    },
    target:
      step.view === 'overview'
        ? null
        : stepCamera(step, {
            viewport: vp,
            asset: input.asset,
            allowFocus: input.allowFocus ?? true,
            motion: input.motion,
          }),
    narrationMs,
    moveMs: (a, b) => panDuration(a, b, vp),
    picture,
    asset: input.asset,
    // A small highlight leans in no closer than the desktop camera ever comes on its own
    // (`CAMERA_MAX_ZOOM`): two thirds of the page stays on show, so it is still this page.
    lean: {
      small: (r, l) => desktopCueSmall(r, l, picture, input.asset),
      // Cut on the shots' clean sides when that holds the marks with air, else centred on them
      // (a mark's own side as a cut puts the window's edge on its ring); centred first when the
      // snapped frame leaves them off to one side (`framesFor`).
      frames: (r) => framesFor(r, lines, CAMERA_MAX_ZOOM),
    },
    // No drift on a desktop or tablet: a slow push toward a corner of a page about the page as a
    // whole cut the sidebar and pointed at nothing (founder, 2026-10-08). Marks are enough there.
    drift: false,
    restMs: DESKTOP_CAMERA_REST_MS,
    entry: entry ?? null,
    calm: { pan, shift },
  });
}

/**
 * Desktop: where a step's camera starts. On the screen the viewer is already looking at
 * (`sameScreen`), coming from a step that followed its narration (`cuePlanDesktop`), it carries
 * on from where that step left it (`prevEnd`); anything else opens on the whole screen (null).
 * The player passes the camera as it is; the exporter and the checks the step before's plan.
 */
export function desktopEntry(
  prev: Step | null | undefined,
  prevEnd: Layout | null | undefined,
  step: Step,
  assets: Record<string, Pick<Asset, 'w' | 'h'>>,
  motion: Motion | undefined,
): Layout | null {
  if (!prev || !prevEnd || !prev.cues?.length || !guidedStep(prev, motion)) return null;
  if (!step.cues?.length || !guidedStep(step, motion)) return null;
  return sameScreen(prev, step, assets) ? prevEnd : null;
}

/**
 * Every desktop cue plan along `path`, each carrying on from the one before on the same screen
 * (`desktopEntry`), as Watch plays them: what the checks judge is what the player shows. Steps
 * that do not follow their narration on a desktop have none.
 */
export function desktopCuePlans(
  path: readonly Step[],
  assets: Record<string, Asset>,
  input: Omit<GuideInput, 'asset'>,
  lineMs: (step: Step) => number | null | undefined,
): Map<Step, CuePlan> {
  const out = new Map<Step, CuePlan>();
  let prev: Step | null = null;
  let prevEnd: Layout | null = null;
  for (const step of path) {
    const asset = assets[step.asset];
    if (!asset || step.scroll || !step.cues?.length || !guidedStep(step, input.motion)) {
      prev = step;
      prevEnd = null;
      continue;
    }
    const entry = desktopEntry(prev, prevEnd, step, assets, input.motion);
    const plan = cuePlanDesktop(step, { ...input, asset }, lineMs(step), entry);
    out.set(step, plan);
    prev = step;
    prevEnd = plan.beats[plan.beats.length - 1]!.layout;
  }
  return out;
}

/** How far a lean's marks may sit from the window's middle, as a share of its width or height. */
export const CUE_OFF_CENTRE = 0.15;

/** How far `r`'s middle sits from the window's middle in layout `l`, as a share of the window. */
function offCentre(l: Layout, r: NormRect, vp: Viewport, asset: Pick<Asset, 'w' | 'h'>): number {
  const cx = l.tx + (r.x + r.w / 2) * asset.w * l.scale;
  const cy = l.ty + (r.y + r.h / 2) * asset.h * l.scale;
  return Math.max(Math.abs(cx - vp.w / 2) / vp.w, Math.abs(cy - vp.h / 2) / vp.h);
}

/** `outer` holds `inner` (within a hair). */
function rectContains(outer: NormRect, inner: NormRect): boolean {
  const e = 0.002;
  return (
    inner.x >= outer.x - e &&
    inner.y >= outer.y - e &&
    inner.x + inner.w <= outer.x + outer.w + e &&
    inner.y + inner.h <= outer.y + outer.h + e
  );
}

/** A rect grown by `px` screen px at `scale` on each side, clipped to the screen. */
function growRect(r: NormRect, px: number, scale: number, asset: Pick<Asset, 'w' | 'h'>): NormRect {
  const gx = px / (scale * asset.w);
  const gy = px / (scale * asset.h);
  const x = Math.max(0, r.x - gx);
  const y = Math.max(0, r.y - gy);
  return { x, y, w: Math.min(1, r.x + r.w + gx) - x, h: Math.min(1, r.y + r.h + gy) - y };
}

function insetRect(p: PxRect, px: number): PxRect {
  return { x: p.x + px, y: p.y + px, w: Math.max(1, p.w - 2 * px), h: Math.max(1, p.h - 2 * px) };
}

/**
 * A `zoom` cue's framing in a phone story: the rect with `CUE_AIR_PX` round
 * it held inside the picture (`keep`), so its ring is never cut by the
 * window's edge — the rect alone fills it edge to edge. The air is in screen
 * px, so it is sized at a first pass's zoom (the second only zooms out: more).
 */
function cueShot(picture: PxRect, asset: ShotAsset, r: NormRect, whole = false): Layout {
  // No closer than `CUE_MAX_ZOOM` times the whole screen: a small rect (a
  // select, a title) otherwise fills the window at a size that reads as a
  // blow-up, not as the app. The rect is grown round its centre to that span.
  const fit = Math.min(picture.w / asset.w, picture.h / asset.h);
  const span = (px: number, a: number, start: number, len: number): [number, number] => {
    const want = Math.min(1, px / (fit * CUE_MAX_ZOOM * a));
    if (len >= want) return [start, len];
    const s0 = clamp(start + len / 2 - want / 2, 0, 1 - want);
    return [s0, want];
  };
  const [x, w] = span(picture.w, asset.w, r.x, r.w);
  const [y, h] = span(picture.h, asset.h, r.y, r.h);
  const shot = { x, y, w, h };
  const first = shotLayout(picture, asset, shot, { keep: r });
  const wholeLayout = shotLayout(picture, asset, shot, {
    keep: growRect(r, CUE_AIR_PX, first.scale, asset),
  });
  // Zoomed out as far as text stays readable on a phone, the whole area and its ring fit.
  // `whole`: always (a window with the screen's shape, where a cut card read worse than a small
  // one, founder 2026-10-21).
  if (whole || wholeLayout.textPx >= PHONE_MIN_TEXT_PX + 0.05) return wholeLayout;
  // Seen whole, the area's text is too small to read (a panel half a desktop screen wide on a
  // 390 px phone). Read it instead: at legible text, from its top-left corner, the way the eye
  // starts a block. Its mark is drawn clipped to the window (`markCues`), never off its edge.
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  const s = MIN_TEXT_PX / textH;
  // Too wide (or tall) for the window at `MIN_TEXT_PX`, but whole, ring and air included, at
  // text still readable on a phone (`PHONE_MIN_TEXT_PX`): zoom out that far and show it all.
  const lead = 2 * (CUE_AIR_PX + SHOT_LEAD_PX);
  const fits = Math.min(
    (picture.w - lead) / Math.max(1e-6, r.w * asset.w),
    (picture.h - lead) / Math.max(1e-6, r.h * asset.h),
  );
  if (fits < s && fits * textH >= PHONE_MIN_TEXT_PX + 0.05) {
    return shotLayout(picture, asset, r, { keep: growRect(r, CUE_AIR_PX, fits, asset) });
  }
  const air = (px: number, size: number): number => px / (s * size);
  const rx = Math.max(0, r.x - air(CUE_AIR_PX, asset.w));
  const ry = Math.max(0, r.y - air(CUE_AIR_PX, asset.h));
  const start = {
    x: rx,
    y: ry,
    w: Math.min(r.x + r.w - rx, picture.w / (s * asset.w)),
    h: Math.min(r.y + r.h - ry, picture.h / (s * asset.h)),
  };
  return shotLayout(picture, asset, start, { legible: true });
}

/**
 * `cuePlan` in a phone story's picture: the whole screen first (as
 * phone-overview does), a `zoom` cue frames its rect inside the picture,
 * the control is the story's own close-up (`target`; null for an overview),
 * and moves take a phone's pace.
 */
export function cuePlanStory(
  step: Step,
  picture: PxRect,
  asset: ShotAsset,
  target: Layout | null,
  narrationMs: number | null | undefined,
  viewport: Viewport,
  /** What the whole screen is (`overviewFrame`). Absent: the full capture. */
  frame?: NormRect,
): CuePlan {
  return cuePlan(step, {
    whole: overviewLayout(picture, asset, frame),
    frame: (r) => cueShot(picture, asset, r),
    target,
    narrationMs,
    moveMs: (a, b) => walkMoveDuration(a, b, viewport),
    picture,
    asset,
    frameOffScreen: true,
    clipMarks: true,
    drift: true,
    relax: false,
    settleMs: WATCH_HOLD_MIN_MS,
  });
}

/* ── phone track ─────────────────────────────────────────────────────────────
 * What a phone shows of a step, planned once at publish and saved with it
 * (`Step.phone`, docs/plans/phone-track.md), instead of worked out by the
 * player as it plays. Planned on a reference phone; each frame's rect is
 * fitted into the real phone's window, so it reads the same on every phone.
 * The player and the 9:16 export play it with `phonePlan`. */

/** The phone a track is planned on: an iPhone 13's portrait window. */
export const PHONE_REF: Viewport = { w: 390, h: 664 };
/**
 * A frame never comes closer than this text size on the reference phone: closer, a small
 * control fills the window alone, out of its place on the page (founder, 2026-10-12: a lone
 * "Edit" and "All jobs" blown up for seconds).
 */
export const PHONE_MAX_TEXT_PX = 16;
/** Two frames that overlap this much of the larger are one. */
const PHONE_SAME_OVERLAP = 0.85;

/**
 * The readable minimum on a phone (px on the reference phone): the quality gate's
 * `phone-unreadable` floor. A frame for a mark zooms out as far as this to hold the mark whole.
 */
export const PHONE_MIN_TEXT_PX = MIN_TEXT_PX - 0.5;

/**
 * Is a mark on `rect` seen whole in the window showing `seen`? A pointer (`look`) only needs
 * its centre; a ring needs all of the rect. A rect bigger than the window is not seen whole: it
 * is drawn clipped to the window (`markCues`), never run off its edge (founder, 2026-10-12).
 */
export function rectShown(rect: NormRect, seen: NormRect, centreOnly: boolean): boolean {
  if (centreOnly) {
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    return cx >= seen.x && cx <= seen.x + seen.w && cy >= seen.y && cy <= seen.y + seen.h;
  }
  return rectWithin(rect, seen);
}

/** The window shows where `rect` starts (its top-left corner): an area too big for it is read from there. */
function rectStartShown(rect: NormRect, seen: NormRect): boolean {
  const e = 0.002;
  return (
    rect.x >= seen.x - e &&
    rect.x <= seen.x + seen.w &&
    rect.y >= seen.y - e &&
    rect.y <= seen.y + seen.h
  );
}

/**
 * The hotspot Watch mode taps on a step with several: the one whose destination is the step's
 * own `next` (the authored main line), else the first. `h.next ?? null` because a hotspot
 * without its own `next` inherits the step's. Shared by the player and the export.
 */
export function primaryHotspot(
  step: Pick<Step, 'hotspots'> & { next?: Step['next'] },
): Hotspot | null {
  const main = step.next ?? null;
  return step.hotspots.find((h) => (h.next ?? null) === main) ?? step.hotspots[0] ?? null;
}

/** A `look` rect holding the tap target counts as on it up to this many times its area. */
export const LOOK_TARGET_MAX_AREA = 4;

/** Does a `look` on `rect` point at the step's tap target (what Watch then presses)? */
export function lookOnTarget(
  step: Pick<Step, 'hotspots'> & { next?: Step['next'] },
  rect: NormRect,
): boolean {
  const h = primaryHotspot(step);
  if (!h) return false;
  const g = h.geom;
  if (rectInside(rect, g)) return true;
  return rectInside(g, rect) && rect.w * rect.h <= LOOK_TARGET_MAX_AREA * g.w * g.h;
}

/**
 * The effect a cue is drawn with. The cursor only ever moves to what it taps: a `look` at the
 * step's tap target is the pointer (it rests there until the press); a `look` at anything else
 * is drawn as a highlight (on a phone a fingertip on it reads as a tap that did nothing,
 * founder, 2026-10-12).
 */
export function markEffect(
  step: Pick<Step, 'hotspots'> & { next?: Step['next'] },
  cue: Pick<Cue, 'rect' | 'effect'>,
): CueEffect {
  return cue.effect === 'look' && !lookOnTarget(step, cue.rect) ? 'highlight' : cue.effect;
}

/** A planned cue's mark as it is drawn: its effect, and its rect clipped to the frame. */
export function cueMark(t: TimedCue): Cue {
  return t.mark ?? t.cue;
}

/** The part of `r` inside `seen`; a side cut by the window is pulled in by the ring's air. */
function clipToSeen(r: NormRect, seen: NormRect, ax: number, ay: number): NormRect | null {
  const e = 0.002;
  const x0 = r.x < seen.x - e ? seen.x + ax : r.x;
  const y0 = r.y < seen.y - e ? seen.y + ay : r.y;
  const x1 = r.x + r.w > seen.x + seen.w + e ? seen.x + seen.w - ax : r.x + r.w;
  const y1 = r.y + r.h > seen.y + seen.h + e ? seen.y + seen.h - ay : r.y + r.h;
  return x1 - x0 > 1e-4 && y1 - y0 > 1e-4 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/** Every layout the camera holds while a mark shows: the one at its start, and any it moves to. */
function layoutsDuring(beats: readonly CueBeat[], startMs: number, endMs: number): Layout[] {
  let first = beats[0]!.layout;
  const out: Layout[] = [];
  for (const b of beats) {
    if (b.atMs <= startMs) first = b.layout;
    else if (b.atMs < endMs) out.push(b.layout);
  }
  return [first, ...out];
}

/**
 * Settle how each planned cue is drawn (`TimedCue.mark`): its effect (`markEffect`) and, on a
 * phone (`clip`), its rect. A ring whose area the window cannot hold whole is drawn as the part
 * of it inside the window, inset by `CUE_AIR_PX` where the window cuts it, so no edge of a mark
 * is ever off screen; the frame reads the area from its start. Mutates and returns `plan`.
 */
function markCues(
  step: Pick<Step, 'hotspots'> & { next?: Step['next'] },
  plan: CuePlan,
  clip: { picture: PxRect; asset: Pick<Asset, 'w' | 'h'> } | null,
): CuePlan {
  for (const t of plan.cues) {
    const effect = markEffect(step, t.cue);
    let rect = t.cue.rect;
    if (clip && effect !== 'look') {
      for (const l of layoutsDuring(plan.beats, t.startMs, t.endMs)) {
        const seen = visibleRect(l, clip.picture, clip.asset);
        if (rectWithin(rect, seen)) continue;
        const k = Math.max(1e-6, l.scale);
        const c = clipToSeen(
          rect,
          seen,
          CUE_AIR_PX / (k * clip.asset.w),
          CUE_AIR_PX / (k * clip.asset.h),
        );
        // Nothing of it in this frame: left whole, and the quality gate says so.
        if (c) rect = c;
      }
    }
    t.mark = effect === t.cue.effect && rect === t.cue.rect ? t.cue : { ...t.cue, effect, rect };
  }
  return plan;
}

/** The part of the asset a layout shows in `picture`, clipped to the asset. */
export function visibleRect(l: Layout, picture: PxRect, asset: Pick<Asset, 'w' | 'h'>): NormRect {
  const x0 = Math.max(0, (picture.x - l.tx) / (l.scale * asset.w));
  const y0 = Math.max(0, (picture.y - l.ty) / (l.scale * asset.h));
  const x1 = Math.min(1, (picture.x + picture.w - l.tx) / (l.scale * asset.w));
  const y1 = Math.min(1, (picture.y + picture.h - l.ty) / (l.scale * asset.h));
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

/** `r` grown round its centre by `k`, kept inside the asset. */
function growInside(r: NormRect, k: number): NormRect {
  const w = Math.min(1, r.w * k);
  const h = Math.min(1, r.h * k);
  return {
    x: clamp(r.x + r.w / 2 - w / 2, 0, 1 - w),
    y: clamp(r.y + r.h / 2 - h / 2, 0, 1 - h),
    w,
    h,
  };
}

/** All of `a` lies inside `b` (a hair of tolerance). */
function rectWithin(a: NormRect, b: NormRect): boolean {
  const e = 0.002;
  return (
    a.x >= b.x - e && a.y >= b.y - e && a.x + a.w <= b.x + b.w + e && a.y + a.h <= b.y + b.h + e
  );
}

/** At least half of `a` lies inside `b`. */
export function rectInside(a: NormRect, b: NormRect): boolean {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 && (w * h) / Math.max(1e-9, a.w * a.h) >= 0.5;
}

function sameRect(a: NormRect, b: NormRect): boolean {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  if (w <= 0 || h <= 0) return false;
  return (w * h) / Math.max(a.w * a.h, b.w * b.h) >= PHONE_SAME_OVERLAP;
}

/**
 * Plan one step's phone track: a glance at the whole screen when the screen is new, an area at
 * readable text for each narration cue (from its phrase), the step's own shots for an overview
 * step without cues, and the control last for a step with one. No frame is closer than
 * `PHONE_MAX_TEXT_PX`. Null for a scroll step (its framing is its scroll).
 */
export function planPhone(
  step: Step,
  asset: ShotAsset,
  opts: {
    /** The step before it, and the demo's assets: a step on the same screen gets no glance. */
    prev?: Step | null;
    assets?: Record<string, Pick<Asset, 'w' | 'h'>>;
    /** What the whole screen is (`overviewFrame`). */
    frame?: NormRect;
    /**
     * The window the track is played in, in phone px, when it is not a phone's (a 9:16 video
     * whose window has the screen's shape): frames are planned for it, so a card fills it.
     */
    picture?: { w: number; h: number };
  } = {},
): PhoneTrack | null {
  if (step.scroll) return null;
  const picture = opts.picture ? { x: 0, y: 0, ...opts.picture } : walkFrame(PHONE_REF);
  const textH = (asset.medianTextH ?? DEFAULT_MEDIAN_TEXT_H) * asset.h;
  const whole = opts.frame ?? FULL_FRAME;
  const frames: PhoneFrame[] = [];
  const same = !!opts.prev && !!opts.assets && sameScreen(opts.prev, step, opts.assets);
  if (!same) frames.push({ kind: 'glance', rect: whole });
  const add = (l: Layout, kind: PhoneFrame['kind'], phrase?: string): void => {
    let rect = visibleRect(l, picture, asset);
    if (rect.w <= 0 || rect.h <= 0) return;
    const text = textH * l.scale;
    if (text > PHONE_MAX_TEXT_PX) rect = growInside(rect, text / PHONE_MAX_TEXT_PX);
    const last = frames[frames.length - 1];
    if (last && last.kind !== 'glance' && sameRect(last.rect, rect)) {
      // One frame; a control keeps its kind (it is where the step rests).
      if (kind === 'control') frames[frames.length - 1] = { ...last, kind: 'control' };
      return;
    }
    frames.push({ kind, rect, ...(phrase ? { phrase } : {}) });
  };
  const view = stepView(step);
  const subject = view === 'action' ? storySubject(step) : null;
  for (const c of step.cues ?? []) {
    // A pointer at the step's own control is the control frame's to show: a frame of its own
    // would move the camera twice to one place as the line ends.
    if (subject && markEffect(step, c) === 'look') continue;
    add(cueShot(picture, asset, c.rect, !!opts.picture), 'read', c.phrase);
  }
  const beats = storyBeats(step, picture, asset, undefined, {
    lead: false,
    ...(opts.frame ? { frame: opts.frame } : {}),
  });
  if (!step.cues?.length && view === 'overview') {
    for (const b of beats.slice(1)) add(b.layout, 'read');
  }
  if (view === 'action' && step.hotspots.length) {
    // A control too wide to read whole (a list row as the target) is read from its start, like
    // an area; the story's target shot would fit it whole at unreadable text (2026-10-12).
    const wide =
      subject && subject.w * asset.w * (MIN_TEXT_PX / textH) > picture.w - 2 * CUE_AIR_PX;
    add(wide ? cueShot(picture, asset, subject) : beats[beats.length - 1]!.layout, 'control');
  }
  if (!frames.length) frames.push({ kind: 'glance', rect: whole });
  return { frames: frames.slice(0, 12), by: 'auto' };
}

/** Plan every step that has no creator-edited track. Pure: returns a new document. */
export function planPhoneTracks<D extends { steps: Step[]; assets: Record<string, ShotAsset> }>(
  doc: D,
  picture?: { w: number; h: number },
): D {
  const frame = overviewFrame(doc);
  return {
    ...doc,
    steps: doc.steps.map((s, i) => {
      if (s.phone?.by === 'edited') return s;
      const asset = doc.assets[s.asset];
      const track = asset
        ? planPhone(s, asset, {
            prev: doc.steps[i - 1] ?? null,
            assets: doc.assets,
            frame,
            ...(picture ? { picture } : {}),
          })
        : null;
      if (track) return { ...s, phone: track };
      const { phone: _gone, ...rest } = s;
      return rest;
    }),
  };
}

/**
 * Play a step's phone track (player on a phone; the 9:16 export): its frames as camera beats on
 * the narration's clock, the cues' marks timed as `cuePlan` times them. A `read` frame moves on
 * its phrase (`CUE_LEAD_MS` before it); the first, when its phrase is late, right after the
 * glance (`WATCH_HOLD_MIN_MS`), so a line is never told over a screen too small to read; one
 * without a phrase follows the frame before after `CUE_HOLD_MS`. The `control` comes as the
 * line nears its end (`GUIDE_TARGET_MIN_MS`), as with the live camera.
 */
export function phonePlan(
  step: Step & { phone: PhoneTrack },
  picture: PxRect,
  asset: ShotAsset,
  narrationMs: number | null | undefined,
  viewport: Viewport,
): CuePlan {
  const cues = step.cues ?? [];
  const text = step.narration?.text ?? cues.map((c) => c.phrase).join(' ');
  const lineMs = narrationMs && narrationMs > 0 ? narrationMs : readingMs(text);
  const subject = storySubject(step);
  // The control stays whole in its frame when the frame was drawn round it; a control wider
  // than a readable frame (a whole list row) is read from its start instead, like any area.
  const layoutOf = (f: PhoneFrame): Layout =>
    f.kind === 'glance'
      ? overviewLayout(picture, asset, f.rect)
      : shotLayout(
          picture,
          asset,
          f.rect,
          f.kind === 'control' && subject && rectWithin(subject, f.rect) ? { keep: subject } : {},
        );
  const frames = step.phone.frames;
  const f0 = frames[0]!;
  const first = layoutOf(f0);
  const beats: CueBeat[] = [
    { atMs: 0, layout: first, reason: f0.kind === 'control' ? 'target' : 'establish' },
  ];
  let targetMs: number | null = f0.kind === 'control' ? 0 : null;
  let cur = first;
  let free = 0;
  let lastAt = 0;
  const arrivals: { phrase?: string; atMs: number }[] = [];
  frames.forEach((f, i) => {
    if (i === 0) return;
    const to = layoutOf(f);
    let at: number;
    if (f.kind === 'control') {
      // Straight after the glance (nothing to read first): the control right away, not at the
      // line's end; the whole screen held over the line was unreadable (2026-10-12).
      at =
        i === 1 && f0.kind === 'glance'
          ? WATCH_HOLD_MIN_MS
          : Math.max(lastAt + CUE_HOLD_MS, lineMs - GUIDE_TARGET_MIN_MS);
      // When the line names the control ("Jobs holds your postings"), the camera arrives on
      // the word, so the pointer at it is drawn where the viewer is looking.
      const named =
        subject && cues.find((c) => rectInside(c.rect, subject) && rectInside(subject, c.rect));
      const spoken = named ? (named.atMs ?? phraseAtMs(text, named.phrase, lineMs)) : null;
      if (spoken !== null) {
        const onWord = Math.max(
          lastAt + CUE_MIN_GAP_MS,
          spoken - walkMoveDuration(cur, to, viewport),
        );
        at = Math.max(
          Math.min(at, onWord),
          i === 1 && f0.kind === 'glance' ? WATCH_HOLD_MIN_MS : 0,
        );
      }
    } else {
      const spoken = f.phrase ? phraseAtMs(text, f.phrase, lineMs) : null;
      // Early enough to arrive on the word (see `cuePlan`).
      at =
        spoken !== null
          ? Math.max(0, spoken - walkMoveDuration(cur, to, viewport))
          : lastAt + (i === 1 ? WATCH_HOLD_MIN_MS : CUE_HOLD_MS);
      if (i === 1 && f0.kind === 'glance' && at > WATCH_HOLD_MIN_MS + CUE_HOLD_MS) {
        at = WATCH_HOLD_MIN_MS;
      }
    }
    if (i > 1) at = Math.max(at, lastAt + CUE_MIN_GAP_MS);
    at = Math.max(at, free);
    beats.push({ atMs: at, layout: to, reason: f.kind === 'control' ? 'target' : 'cue' });
    if (f.kind === 'control') targetMs = at;
    free = at + walkMoveDuration(cur, to, viewport);
    arrivals.push({ ...(f.phrase ? { phrase: f.phrase } : {}), atMs: free });
    cur = to;
    lastAt = at;
  });
  // When each beat's camera has arrived (the first is there from the start).
  const arrivedAt = beats.map((_, i) => (i === 0 ? 0 : arrivals[i - 1]!.atMs));
  const seenIn = (b: CueBeat): NormRect => visibleRect(b.layout, picture, asset);
  const pointer = (cue: Cue): boolean => markEffect(step, cue) === 'look';
  const shows = (b: CueBeat, cue: Cue): boolean => rectShown(cue.rect, seenIn(b), pointer(cue));
  // An area too big for any frame is read from its start: the frame that shows its top-left.
  const starts = (b: CueBeat, cue: Cue): boolean =>
    !pointer(cue) && rectStartShown(cue.rect, seenIn(b));
  // The marks, as `cuePlan` times them: from the phrase, once a frame showing the mark is there.
  // A mark drawn where the camera is not yet looking rides in from off screen, or never shows
  // (the pointer at a control the camera reaches later, 2026-10-12).
  const timed: TimedCue[] = [];
  let prevAt = -Infinity;
  for (const cue of cues) {
    const spoken = cue.atMs ?? phraseAtMs(text, cue.phrase, lineMs);
    let at = Math.max(spoken ?? prevAt + CUE_HOLD_MS, prevAt + CUE_MIN_GAP_MS, 0);
    let k = 0;
    for (let i = 1; i < beats.length && beats[i]!.atMs <= at; i++) k = i;
    const from = k;
    const find = (ok: (b: CueBeat, cue: Cue) => boolean): number =>
      ok(beats[from]!, cue) ? from : beats.findIndex((b, i) => i > from && ok(b, cue));
    const whole = find(shows);
    // Read from its start: a frame already on its way at the phrase carries the mark in with it
    // (clipped to where it lands), a later one is waited for.
    const start = whole < 0 ? find(starts) : -1;
    if (whole >= 0 || start > from) {
      k = whole >= 0 ? whole : start;
      at = Math.max(at, arrivedAt[k]! - CUE_LEAD_MS);
    }
    const prev = timed[timed.length - 1];
    if (prev) prev.endMs = Math.max(prev.startMs, at);
    timed.push({ cue, startMs: at, endMs: lineMs });
    prevAt = at;
  }
  // A mark goes as the camera leaves the frame that showed it: it would ride the pan off the
  // window's edge.
  for (const t of timed) {
    const leaves = beats.find((b) => b.atMs > t.startMs);
    if (leaves) t.endMs = Math.max(t.startMs, Math.min(t.endMs, leaves.atMs - CUE_FADE_MS));
  }
  return markCues(step, { beats, cues: timed, targetMs }, { picture, asset });
}
